import { ART_ASSETS, BATTLE_ASSETS, DEFAULT_COUNTER_ATTACK_CONFIG, DEFAULT_GAME_CONFIG, preloadImages } from './config';
import { applyJumpDamage, createCombatState, healEnemy, hpPercent, type CombatState } from './game/combat';
import { DodgeDetector } from './game/dodgeDetector';
import { JumpDetector } from './game/jumpDetector';
import { createResult, formatDuration, RoundTimer } from './game/session';
import { requestMotionPermission, MotionStream } from './sensors/motion';
import type { DodgeDirection, ExerciseId, GameConfig, GameResult, HapticEvery, MotionSample, SensorMode } from './types';
import { maybeVibrateForJump, vibrateForCounterCountdown, vibrateForDodgeFailure, vibrateForDodgeSuccess, vibrateForDodgeWarning, vibrateForTap, vibrateForVictory } from './ui/haptics';

type Screen = 'select' | 'workouts' | 'preflight' | 'calibrating' | 'battle' | 'paused' | 'results' | 'home' | 'tasks' | 'rank' | 'streak' | 'me' | 'settings' | 'outfit' | 'rewards' | 'progress';
type EnemyPose = 'idle' | 'hit' | 'defeated';
type Outfit = 'teal' | 'sunny' | 'coral' | 'legendary';
type JumpTarget = 100 | 300 | 1000;
type ExerciseTarget = 20 | 50 | 60 | 100 | 200 | 300 | 500 | 1000;
type WakeLockSentinelLike = { release: () => Promise<void> };
type NavigatorWithWakeLock = Navigator & { wakeLock?: { request: (type: 'screen') => Promise<WakeLockSentinelLike> } };
type TimedLeaderboardScope = 'today' | 'month' | 'total';
type LeaderboardBoard = 'xp' | 'damage' | 'breaks' | 'kcal';
type DndWindow = { start: string; end: string };
type Gender = 'unset' | 'female' | 'male' | 'nonbinary';
type BattlePhase = 'ACTIVE_ATTACK' | 'COUNTER_COUNTDOWN' | 'DODGE_WINDOW' | 'DODGE_SUCCESS' | 'DODGE_FAILED';
type CounterTrigger = { id: 'first' | 'second'; thresholdHp: number; consumed: boolean };
type MotionVector = { x: number; y: number; z: number };
type LeaderboardPlayer = {
  playerId: string;
  name: string;
  outfit: Outfit | string;
  level?: number;
  todayXp?: number;
  monthXp?: number;
  totalXp?: number;
  todayDamage?: number;
  monthDamage?: number;
  totalDamage?: number;
  todayCalories: number;
  monthCalories: number;
  totalCalories: number;
  todayBreaks?: number;
  monthBreaks?: number;
  breaks: number;
  streak: number;
  workouts: number;
  score?: number;
};

const outfitLabels: Record<Outfit, string> = { teal: 'Active Teal', sunny: 'Sunny Runner', coral: 'Coral Boxer', legendary: 'Legendary Neon' };
const outfitPrices: Record<Outfit, number> = { teal: 0, sunny: 100, coral: 500, legendary: 3000 };
const outfitAssets: Record<Outfit, string> = {
  teal: '/assets/optimized/outfit-teal.webp',
  sunny: '/assets/optimized/outfit-sunny.webp',
  coral: '/assets/optimized/outfit-coral.webp',
  legendary: '/assets/optimized/outfit-legendary.webp'
};
const jumpTargets: JumpTarget[] = [100, 300, 1000];
const assumedBodyWeightKg = 70;
const runningMetersPerRep = 0.38;
const baseXpPerLevel = 100;
const xpGrowthPerLevel = 1.6;
const exerciseSpecs: Record<ExerciseId, {
  id: ExerciseId;
  title: string;
  shortTitle: string;
  targets: readonly ExerciseTarget[];
  defaultTarget: ExerciseTarget;
  met: number;
  rpm: number;
  icon: string;
  actionIcon: string;
  instruction: string;
  battleVerb: string;
  countLabel: string;
  detector: ConstructorParameters<typeof JumpDetector>[0];
}> = {
  jump_rope: {
    id: 'jump_rope',
    title: 'Jump Rope Battle',
    shortTitle: 'Jump Rope',
    targets: [100, 300, 1000],
    defaultTarget: 100,
    met: 11.8,
    rpm: 120,
    icon: ART_ASSETS.exerciseIcons.jump_rope,
    actionIcon: ART_ASSETS.exerciseActions.jump_rope,
    instruction: 'Hold the phone like a rope handle and jump naturally.',
    battleVerb: 'Whip the Desk Monster!',
    countLabel: 'reps',
    detector: { ...DEFAULT_GAME_CONFIG, minValidIntervalMs: 280, maxValidIntervalMs: 1200, minPeakSignal: 2.0, enableGyroAssist: true }
  },
  squat: {
    id: 'squat',
    title: 'Squat Battle',
    shortTitle: 'Squat',
    targets: [20, 50, 100],
    defaultTarget: 20,
    met: 5.0,
    rpm: 24,
    icon: ART_ASSETS.exerciseIcons.squat,
    actionIcon: ART_ASSETS.exerciseActions.squat,
    instruction: 'Hold the phone level with your chest, squat down, then stand tall.',
    battleVerb: 'Drop low and crush the Desk Monster!',
    countLabel: 'squats',
    detector: { ...DEFAULT_GAME_CONFIG, minValidIntervalMs: 900, maxValidIntervalMs: 3200, minPeakSignal: 1.35, noiseMultiplier: 3.0, gyroHardSpinDps: 520, gyroAssistMaxDps: 180, enableGyroAssist: true, requireNegativeRecovery: true, negativeRecoveryRatio: 0.35 }
  },
  boxing: {
    id: 'boxing',
    title: 'Boxing Rush',
    shortTitle: 'Boxing',
    targets: [100, 300, 1000],
    defaultTarget: 100,
    met: 7.8,
    rpm: 160,
    icon: ART_ASSETS.exerciseIcons.boxing,
    actionIcon: ART_ASSETS.exerciseActions.boxing,
    instruction: 'Hold the phone firmly and punch forward. One clear punch counts once.',
    battleVerb: 'Punch through the Desk Monster!',
    countLabel: 'punches',
    detector: { ...DEFAULT_GAME_CONFIG, minValidIntervalMs: 380, maxValidIntervalMs: 1200, minPeakSignal: 3.0, noiseMultiplier: 3.2, gyroHardSpinDps: 900, gyroAssistMaxDps: 420, enableGyroAssist: true, requireNegativeRecovery: true, negativeRecoveryRatio: 0.45 }
  },
  jumping_jacks: {
    id: 'jumping_jacks',
    title: 'Jumping Jacks',
    shortTitle: 'Jacks',
    targets: [60, 200, 500],
    defaultTarget: 60,
    met: 7.7,
    rpm: 60,
    icon: ART_ASSETS.exerciseIcons.jumping_jacks,
    actionIcon: ART_ASSETS.exerciseActions.jumping_jacks,
    instruction: 'Hold the phone securely and do jumping jacks. Each open-close jump counts once.',
    battleVerb: 'Spring open and blast the Desk Monster!',
    countLabel: 'jacks',
    detector: { ...DEFAULT_GAME_CONFIG, minValidIntervalMs: 650, maxValidIntervalMs: 2200, minPeakSignal: 2.2, gyroHardSpinDps: 520, gyroAssistMaxDps: 180, enableGyroAssist: true, requireNegativeRecovery: true, negativeRecoveryRatio: 0.4 }
  },
  running: {
    id: 'running',
    title: 'Run in Place',
    shortTitle: 'Run',
    targets: [100, 300, 1000],
    defaultTarget: 100,
    met: 8.0,
    rpm: 150,
    icon: ART_ASSETS.exerciseIcons.running,
    actionIcon: ART_ASSETS.exerciseActions.running,
    instruction: 'Run in place and swing the phone arm naturally. One arm swing counts once.',
    battleVerb: 'Sprint-strike the Desk Monster!',
    countLabel: 'arm swings',
    detector: { ...DEFAULT_GAME_CONFIG, minValidIntervalMs: 360, maxValidIntervalMs: 1300, minPeakSignal: 1.9, noiseMultiplier: 3.0, gyroHardSpinDps: 720, gyroAssistMaxDps: 320, enableGyroAssist: true, requireNegativeRecovery: true, negativeRecoveryRatio: 0.35 }
  }
};
const exerciseIds = Object.keys(exerciseSpecs) as ExerciseId[];
const dodgeSensorSettleMs = 120;
const dodgeInputGraceMs = 280;

export class QuickFitBattleApp {
  private screen: Screen = 'select';
  private config: GameConfig = { ...DEFAULT_GAME_CONFIG };
  private combat: CombatState = createCombatState(DEFAULT_GAME_CONFIG);
  private battlePhase: BattlePhase = 'ACTIVE_ATTACK';
  private counterTriggers: CounterTrigger[] = [];
  private counterCountdown: 3 | 2 | 1 | 0 = 0;
  private counterDirection: DodgeDirection | null = null;
  private lastCounterDirection: DodgeDirection | null = null;
  private dodgeWindowOpenedAt = 0;
  private pendingGraceDodgeDirection: DodgeDirection | null = null;
  private pendingGraceDodgeStrength = 0;
  private dodgeBaseline: MotionVector | null = null;
  private lastCounterHpBeforeHeal = 0;
  private lastCounterHpAfterHeal = 0;
  private counterTimer: ReturnType<typeof setTimeout> | null = null;
  private dodgeDetector = new DodgeDetector(DEFAULT_COUNTER_ATTACK_CONFIG.dodgeDetection);
  private successfulDodges = 0;
  private lastCoinsEarned = 0;
  private timer = new RoundTimer();
  private detector: JumpDetector | null = null;
  private motionStream: MotionStream | null = null;
  private result: GameResult | null = null;
  private sensorMode: SensorMode = 'motion';
  private enemyPose: EnemyPose = 'idle';
  private statusText = "You've been sitting for 60 minutes. Choose a micro-workout.";
  private renderQueued = false;
  private hitTimer: ReturnType<typeof setTimeout> | null = null;
  private ultimateTimer: ReturnType<typeof setTimeout> | null = null;
  private ultimateActive = false;
  private staleTimer: ReturnType<typeof setTimeout> | null = null;
  private victoryTimer: ReturnType<typeof setTimeout> | null = null;
  private calibrationTimer: ReturnType<typeof setTimeout> | null = null;
  private wakeLock: WakeLockSentinelLike | null = null;
  private spinRejectCount = 0;
  private spinRejectWindowStartedAt = 0;
  private lastSpinWarningAt = 0;
  private playerName = 'Active Hero';
  private playerId = '';
  private currentExerciseId: ExerciseId = 'jump_rope';
  private reminderExerciseId: ExerciseId = 'jump_rope';
  private outfit: Outfit = 'teal';
  private ownedOutfits: Outfit[] = ['teal'];
  private coins = 320;
  private gender: Gender = 'unset';
  private birthDate = '';
  private heightCm = 170;
  private weightKg = 70;
  private reminderIntervalMinutes = 30;
  private dndWindows: DndWindow[] = [{ start: '21:00', end: '09:00' }];
  private sitBreaksCompleted = 0;
  private monthlySitBreaksKey = this.monthKey();
  private monthlySitBreaks = 0;
  private totalSitBreaks = 0;
  private workoutsCompleted = 0;
  private wellnessTipRead = false;
  private rewardClaimed = false;
  private streakDates: string[] = [];
  private dailyCaloriesDate = this.dateKey();
  private dailyCalories = 0;
  private monthlyCaloriesKey = this.monthKey();
  private monthlyCalories = 0;
  private totalCalories = 0;
  private lastCaloriesEarned = 0;
  private dailyXp = 0;
  private monthlyXpKey = this.monthKey();
  private monthlyXp = 0;
  private totalXp = 0;
  private lastXpEarned = 0;
  private lastLevelBeforeRound = 1;
  private lastLevelAfterRound = 1;
  private dailyDamage = 0;
  private monthlyDamageKey = this.monthKey();
  private monthlyDamage = 0;
  private totalDamage = 0;
  private lastDamageDealt = 0;
  private streakMonthOffset = 0;
  private xpLeaderboardScope: TimedLeaderboardScope = 'today';
  private breakLeaderboardScope: TimedLeaderboardScope = 'today';
  private leaderboardScope: TimedLeaderboardScope = 'today';
  private damageLeaderboardScope: TimedLeaderboardScope = 'today';
  private expandedLeaderboards = new Set<LeaderboardBoard>();
  private leaderboardPlayers: LeaderboardPlayer[] = [];
  private screenBackStack: Screen[] = [];
  private handlingPopState = false;
  private browserBackDepth = 0;
  private sittingStartedAt = Date.now();
  private sittingElapsedMs = 0;
  private sittingTimer: ReturnType<typeof setInterval> | null = null;
  private reminderBucketSent = 0;
  private backgroundReminderSyncInFlight: Promise<boolean> | null = null;
  private lastSittingRenderMinute = -1;
  private renderedScreen: Screen | null = null;
  private readonly scrollContainer = { current: null as HTMLElement | null };
  private screenScrollTop: Partial<Record<Screen, number>> = {};

  constructor(private readonly root: HTMLElement) {}

  mount(): void {
    document.addEventListener('visibilitychange', this.handleVisibilityChange);
    window.addEventListener('popstate', this.handlePopState);
    window.addEventListener('resize', this.handleViewportChange);
    window.visualViewport?.addEventListener('resize', this.handleViewportChange);
    window.visualViewport?.addEventListener('scroll', this.handleViewportChange);
    this.root.addEventListener('scroll', this.handleScrollCapture, true);
    this.restorePreferences();
    this.resetDailyCaloriesIfNeeded();
    this.redrawReminderWorkout(false);
    this.setupBrowserBackGuard();
    this.updateViewportVars();
    this.startSittingTimer();
    this.syncBackgroundReminderIfEnabled();
    void this.syncLeaderboard();
    this.render();
  }

  destroy(): void {
    document.removeEventListener('visibilitychange', this.handleVisibilityChange);
    window.removeEventListener('popstate', this.handlePopState);
    window.removeEventListener('resize', this.handleViewportChange);
    window.visualViewport?.removeEventListener('resize', this.handleViewportChange);
    window.visualViewport?.removeEventListener('scroll', this.handleViewportChange);
    this.root.removeEventListener('scroll', this.handleScrollCapture, true);
    this.stopMotion();
    this.clearTimers();
    if (this.sittingTimer) clearInterval(this.sittingTimer);
    void this.releaseWakeLock();
  }

  private restorePreferences(): void {
    try {
      const savedId = localStorage.getItem('quickfit-player-id');
      this.playerId = savedId || this.createPlayerId();
      const savedName = localStorage.getItem('quickfit-player-name');
      if (savedName?.trim()) this.playerName = savedName.trim().slice(0, 24);
      const savedOutfit = localStorage.getItem('quickfit-outfit') as Outfit | null;
      if (savedOutfit && savedOutfit in outfitLabels) this.outfit = savedOutfit;
      const savedOwnedOutfits = JSON.parse(localStorage.getItem('quickfit-owned-outfits') || '["teal"]') as unknown;
      if (Array.isArray(savedOwnedOutfits)) this.ownedOutfits = [...new Set(['teal', ...savedOwnedOutfits.filter((item): item is Outfit => typeof item === 'string' && item in outfitLabels)])] as Outfit[];
      const savedGender = localStorage.getItem('quickfit-gender') as Gender | null;
      if (savedGender && ['unset','female','male','nonbinary'].includes(savedGender)) this.gender = savedGender;
      const savedBirthDate = localStorage.getItem('quickfit-birth-date') || '';
      if (/^\d{4}-\d{2}-\d{2}$/.test(savedBirthDate)) this.birthDate = savedBirthDate;
      const savedHeight = Number(localStorage.getItem('quickfit-height-cm'));
      if (Number.isFinite(savedHeight) && savedHeight >= 80 && savedHeight <= 240) this.heightCm = Math.round(savedHeight);
      const savedWeight = Number(localStorage.getItem('quickfit-weight-kg'));
      if (Number.isFinite(savedWeight) && savedWeight >= 25 && savedWeight <= 250) this.weightKg = Math.round(savedWeight);
      const savedInterval = Number(localStorage.getItem('quickfit-reminder-interval-minutes'));
      if (Number.isFinite(savedInterval) && savedInterval >= 10 && savedInterval <= 120) this.reminderIntervalMinutes = Math.round(savedInterval);
      const savedDnd = JSON.parse(localStorage.getItem('quickfit-dnd-windows') || 'null') as unknown;
      if (Array.isArray(savedDnd)) { const clean = this.sanitizeDndWindows(savedDnd); if (clean.length) this.dndWindows = clean; }
      const savedCoins = Number(localStorage.getItem('quickfit-coins'));
      if (Number.isFinite(savedCoins) && savedCoins >= 0) this.coins = savedCoins;
      const savedBreaks = Number(localStorage.getItem('quickfit-breaks'));
      if (Number.isFinite(savedBreaks) && savedBreaks >= 0) this.sitBreaksCompleted = Math.min(3, savedBreaks);
      const savedMonthlyBreaksKey = localStorage.getItem('quickfit-monthly-breaks-key');
      const savedMonthlyBreaks = Number(localStorage.getItem('quickfit-monthly-breaks'));
      if (savedMonthlyBreaksKey === this.monthKey() && Number.isFinite(savedMonthlyBreaks) && savedMonthlyBreaks >= 0) {
        this.monthlySitBreaksKey = savedMonthlyBreaksKey;
        this.monthlySitBreaks = Math.floor(savedMonthlyBreaks);
      }
      const savedTotalBreaks = Number(localStorage.getItem('quickfit-total-breaks'));
      if (Number.isFinite(savedTotalBreaks) && savedTotalBreaks >= 0) this.totalSitBreaks = Math.floor(savedTotalBreaks);
      const savedWorkouts = Number(localStorage.getItem('quickfit-workouts-completed'));
      if (Number.isFinite(savedWorkouts) && savedWorkouts >= 0) this.workoutsCompleted = Math.floor(savedWorkouts);
      this.wellnessTipRead = localStorage.getItem('quickfit-tip-read') === 'true';
      this.rewardClaimed = localStorage.getItem('quickfit-reward-claimed') === 'true';
      const savedStreakDates = JSON.parse(localStorage.getItem('quickfit-streak-dates') || '[]') as unknown;
      if (Array.isArray(savedStreakDates)) {
        this.streakDates = [...new Set(savedStreakDates.filter((date): date is string => typeof date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(date)))].sort();
      }
      const savedTarget = Number(localStorage.getItem('quickfit-target-jumps')) as ExerciseTarget;
      const savedExercise = localStorage.getItem('quickfit-exercise-id') as ExerciseId | null;
      if (savedExercise && savedExercise in exerciseSpecs) this.currentExerciseId = savedExercise;
      if (this.currentSpec().targets.includes(savedTarget as ExerciseTarget)) {
        this.config = this.configForTarget(savedTarget);
        this.combat = createCombatState(this.config);
      }
      const savedHapticRaw = localStorage.getItem('quickfit-haptic-every');
      const savedHapticEvery = Number(savedHapticRaw) as HapticEvery;
      if (savedHapticRaw !== null && [0, 1, 5, 10].includes(savedHapticEvery)) this.config = { ...this.config, hapticEvery: savedHapticEvery === 0 ? 0 : 1 };
      const savedCaloriesDate = localStorage.getItem('quickfit-daily-calories-date');
      const savedCalories = Number(localStorage.getItem('quickfit-daily-calories'));
      if (savedCaloriesDate === this.dateKey() && Number.isFinite(savedCalories) && savedCalories >= 0) {
        this.dailyCaloriesDate = savedCaloriesDate;
        this.dailyCalories = savedCalories;
        const savedDailyXp = Number(localStorage.getItem('quickfit-daily-xp'));
        if (Number.isFinite(savedDailyXp) && savedDailyXp >= 0) this.dailyXp = Math.floor(savedDailyXp);
        const savedDailyDamage = Number(localStorage.getItem('quickfit-daily-damage'));
        if (Number.isFinite(savedDailyDamage) && savedDailyDamage >= 0) this.dailyDamage = Math.floor(savedDailyDamage);
      }
      const savedMonthKey = localStorage.getItem('quickfit-monthly-calories-key');
      const savedMonthlyCalories = Number(localStorage.getItem('quickfit-monthly-calories'));
      if (savedMonthKey === this.monthKey() && Number.isFinite(savedMonthlyCalories) && savedMonthlyCalories >= 0) {
        this.monthlyCaloriesKey = savedMonthKey;
        this.monthlyCalories = savedMonthlyCalories;
      }
      const savedXpMonthKey = localStorage.getItem('quickfit-monthly-xp-key');
      const savedMonthlyXp = Number(localStorage.getItem('quickfit-monthly-xp'));
      if (savedXpMonthKey === this.monthKey() && Number.isFinite(savedMonthlyXp) && savedMonthlyXp >= 0) {
        this.monthlyXpKey = savedXpMonthKey;
        this.monthlyXp = Math.floor(savedMonthlyXp);
      }
      const savedDamageMonthKey = localStorage.getItem('quickfit-monthly-damage-key');
      const savedMonthlyDamage = Number(localStorage.getItem('quickfit-monthly-damage'));
      if (savedDamageMonthKey === this.monthKey() && Number.isFinite(savedMonthlyDamage) && savedMonthlyDamage >= 0) {
        this.monthlyDamageKey = savedDamageMonthKey;
        this.monthlyDamage = Math.floor(savedMonthlyDamage);
      }
      const savedTotalCalories = Number(localStorage.getItem('quickfit-total-calories'));
      if (Number.isFinite(savedTotalCalories) && savedTotalCalories >= 0) this.totalCalories = Math.floor(savedTotalCalories);
      const savedTotalXp = Number(localStorage.getItem('quickfit-total-xp'));
      if (Number.isFinite(savedTotalXp) && savedTotalXp >= 0) this.totalXp = Math.floor(savedTotalXp);
      const savedTotalDamage = Number(localStorage.getItem('quickfit-total-damage'));
      if (Number.isFinite(savedTotalDamage) && savedTotalDamage >= 0) this.totalDamage = Math.floor(savedTotalDamage);
      const savedSittingStartedAt = Number(localStorage.getItem('quickfit-sitting-started-at'));
      if (Number.isFinite(savedSittingStartedAt) && savedSittingStartedAt > 0) this.sittingStartedAt = savedSittingStartedAt;
      const savedReminderBucket = Number(localStorage.getItem('quickfit-reminder-bucket'));
      if (Number.isFinite(savedReminderBucket) && savedReminderBucket >= 0) {
        this.reminderBucketSent = Math.floor(savedReminderBucket);
      } else if (localStorage.getItem('quickfit-reminder-sent') === 'true') {
        this.reminderBucketSent = Math.max(1, Math.floor((Date.now() - this.sittingStartedAt) / (this.reminderIntervalMinutes * 60_000)));
      }
    } catch { /* storage is optional */ }
  }

  private persistProgress(): void {
    try {
      localStorage.setItem('quickfit-player-id', this.playerId);
      localStorage.setItem('quickfit-player-name', this.playerName);
      localStorage.setItem('quickfit-outfit', this.outfit);
      localStorage.setItem('quickfit-owned-outfits', JSON.stringify(this.ownedOutfits));
      localStorage.setItem('quickfit-gender', this.gender);
      localStorage.setItem('quickfit-birth-date', this.birthDate);
      localStorage.setItem('quickfit-height-cm', String(this.heightCm));
      localStorage.setItem('quickfit-weight-kg', String(this.weightKg));
      localStorage.setItem('quickfit-reminder-interval-minutes', String(this.reminderIntervalMinutes));
      localStorage.setItem('quickfit-dnd-windows', JSON.stringify(this.dndWindows));
      localStorage.setItem('quickfit-coins', String(this.coins));
      localStorage.setItem('quickfit-breaks', String(this.sitBreaksCompleted));
      localStorage.setItem('quickfit-monthly-breaks-key', this.monthlySitBreaksKey);
      localStorage.setItem('quickfit-monthly-breaks', String(this.monthlySitBreaks));
      localStorage.setItem('quickfit-total-breaks', String(this.totalSitBreaks));
      localStorage.setItem('quickfit-workouts-completed', String(this.workoutsCompleted));
      localStorage.setItem('quickfit-tip-read', String(this.wellnessTipRead));
      localStorage.setItem('quickfit-reward-claimed', String(this.rewardClaimed));
      localStorage.setItem('quickfit-streak-dates', JSON.stringify(this.streakDates));
      localStorage.setItem('quickfit-exercise-id', this.currentExerciseId);
      localStorage.setItem('quickfit-target-jumps', String(this.config.targetJumps));
      localStorage.setItem('quickfit-haptic-every', String(this.config.hapticEvery));
      localStorage.setItem('quickfit-daily-calories-date', this.dailyCaloriesDate);
      localStorage.setItem('quickfit-daily-calories', String(this.dailyCalories));
      localStorage.setItem('quickfit-daily-xp', String(this.dailyXp));
      localStorage.setItem('quickfit-daily-damage', String(this.dailyDamage));
      localStorage.setItem('quickfit-monthly-calories-key', this.monthlyCaloriesKey);
      localStorage.setItem('quickfit-monthly-calories', String(this.monthlyCalories));
      localStorage.setItem('quickfit-monthly-xp-key', this.monthlyXpKey);
      localStorage.setItem('quickfit-monthly-xp', String(this.monthlyXp));
      localStorage.setItem('quickfit-monthly-damage-key', this.monthlyDamageKey);
      localStorage.setItem('quickfit-monthly-damage', String(this.monthlyDamage));
      localStorage.setItem('quickfit-total-calories', String(this.totalCalories));
      localStorage.setItem('quickfit-total-xp', String(this.totalXp));
      localStorage.setItem('quickfit-total-damage', String(this.totalDamage));
      localStorage.setItem('quickfit-sitting-started-at', String(this.sittingStartedAt));
      localStorage.setItem('quickfit-reminder-bucket', String(this.reminderBucketSent));
      localStorage.removeItem('quickfit-reminder-sent');
    } catch { /* ignore */ }
  }

  private startSittingTimer(): void {
    try {
      if (!localStorage.getItem('quickfit-sitting-started-at')) this.persistProgress();
    } catch {
      // Storage can be unavailable in tests or private browsing.
    }
    this.updateSittingElapsed();
    this.sittingTimer = setInterval(() => {
      this.updateSittingElapsed();
      this.resetDailyCaloriesIfNeeded();
      const reminderBucket = Math.floor(this.sittingElapsedMs / (this.reminderIntervalMinutes * 60_000));
      const shouldSendReminder = reminderBucket >= 1 && reminderBucket > this.reminderBucketSent;
      if (shouldSendReminder) {
        this.reminderBucketSent = reminderBucket;
        this.persistProgress();
        void this.sendSittingReminder(reminderBucket);
      }
      const displayMinute = Math.floor(this.sittingElapsedMs / 60_000);
      if ((shouldSendReminder || displayMinute !== this.lastSittingRenderMinute) && (this.screen === 'select' || this.screen === 'home' || this.screen === 'progress')) {
        this.lastSittingRenderMinute = displayMinute;
        this.queueRender();
      }
    }, 1000);
  }

  private updateSittingElapsed(): void {
    if (this.isInDoNotDisturb()) {
      this.sittingStartedAt = Date.now();
      this.sittingElapsedMs = 0;
      this.reminderBucketSent = 0;
      this.persistProgress();
      return;
    }
    this.sittingElapsedMs = Math.max(0, Date.now() - this.sittingStartedAt);
  }

  private async sendSittingReminder(reminderBucket = Math.max(1, Math.floor(this.sittingElapsedMs / (this.reminderIntervalMinutes * 60_000)))): Promise<void> {
    if (!('Notification' in window)) return;
    let permission = Notification.permission;
    if (permission === 'default') {
      try { permission = await Notification.requestPermission(); } catch { return; }
    }
    if (permission !== 'granted') return;
    if (this.isInDoNotDisturb()) return;
    const minutes = reminderBucket * this.reminderIntervalMinutes;
    const title = 'Haysun sit-break ready';
    const body = `You've been sitting for ${minutes} minutes. Reveal a fresh micro-workout card draw.`;
    const registration = await navigator.serviceWorker?.ready.catch(() => null);
    if (registration?.showNotification) {
      await registration.showNotification(title, { body, icon: '/assets/optimized/app-icon-192.png', badge: '/assets/optimized/app-icon-192.png' });
    } else {
      new Notification(title, { body, icon: '/assets/optimized/app-icon-192.png' });
    }
  }

  private async enableNotifications(): Promise<void> {
    if (!('Notification' in window)) {
      this.statusText = 'This browser does not support notifications, but the in-app sitting timer still works.';
      this.render();
      return;
    }
    try {
      const permission = Notification.permission === 'default' ? await Notification.requestPermission() : Notification.permission;
      if (permission === 'granted') {
        const pushReady = await this.registerBackgroundPush();
        this.statusText = pushReady
          ? `${this.reminderIntervalMinutes}-minute background reminders are on.`
          : `${this.reminderIntervalMinutes}-minute in-app reminders are on. Background push needs VAPID keys on the server.`;
      } else {
        this.statusText = 'Notifications are not enabled. You can still watch the in-app sitting timer.';
      }
    } catch {
      this.statusText = 'Notification permission could not be requested in this browser.';
    }
    this.render();
  }

  private async registerBackgroundPush(): Promise<boolean> {
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) return false;
    if (this.backgroundReminderSyncInFlight) return this.backgroundReminderSyncInFlight;
    this.backgroundReminderSyncInFlight = this.doRegisterBackgroundPush().finally(() => {
      this.backgroundReminderSyncInFlight = null;
    });
    return this.backgroundReminderSyncInFlight;
  }

  private async doRegisterBackgroundPush(): Promise<boolean> {
    const config = await fetch('/api/push/config').then((response) => response.json()).catch(() => null) as { enabled?: boolean; publicKey?: string } | null;
    if (!config?.enabled || !config.publicKey) return false;
    const registration = await this.serviceWorkerReadyWithTimeout();
    if (!registration) return false;
    const applicationServerKey = this.urlBase64ToUint8Array(config.publicKey);
    let subscription = await registration.pushManager.getSubscription();
    if (subscription && this.subscriptionKeyChanged(subscription, applicationServerKey)) {
      await subscription.unsubscribe().catch(() => false);
      subscription = null;
    }
    subscription ??= await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey
    });
    const response = await fetch('/api/push/config', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({
        playerId: this.playerId,
        name: this.playerName,
        subscription,
        intervalMinutes: this.reminderIntervalMinutes,
        dndWindows: this.dndWindows,
        sittingStartedAt: this.sittingStartedAt
      })
    });
    return response.ok;
  }

  private async serviceWorkerReadyWithTimeout(): Promise<ServiceWorkerRegistration | null> {
    return Promise.race([
      navigator.serviceWorker.ready,
      new Promise<null>((resolve) => setTimeout(() => resolve(null), 8000))
    ]);
  }

  private syncBackgroundReminderIfEnabled(): void {
    if (!('Notification' in window) || Notification.permission !== 'granted') return;
    void this.registerBackgroundPush();
  }

  private subscriptionKeyChanged(subscription: PushSubscription, expectedKey: ArrayBuffer): boolean {
    const currentKey = subscription.options?.applicationServerKey;
    if (!currentKey) return false;
    const current = new Uint8Array(currentKey);
    const expected = new Uint8Array(expectedKey);
    return current.length !== expected.length || current.some((value, index) => value !== expected[index]);
  }

  private urlBase64ToUint8Array(value: string): ArrayBuffer {
    const padding = '='.repeat((4 - value.length % 4) % 4);
    const base64 = (value + padding).replace(/-/g, '+').replace(/_/g, '/');
    const raw = window.atob(base64);
    const output = Uint8Array.from([...raw].map((char) => char.charCodeAt(0)));
    return output.buffer.slice(output.byteOffset, output.byteOffset + output.byteLength) as ArrayBuffer;
  }

  private readonly handleVisibilityChange = (): void => {
    if (document.hidden && this.screen === 'battle') this.pauseBattle('The page moved to the background, so the timer is paused.');
  };

  private readonly handleViewportChange = (): void => {
    this.updateViewportVars();
  };

  private readonly handleScrollCapture = (event: Event): void => {
    const target = event.target as HTMLElement | null;
    if (!target || !target.classList.contains('app-shell')) return;
    this.screenScrollTop[this.screen] = target.scrollTop;
  };

  private getScrollContainer(): HTMLElement | null {
    if (this.scrollContainer.current && document.contains(this.scrollContainer.current)) return this.scrollContainer.current;
    this.scrollContainer.current = this.root.querySelector('.app-shell');
    return this.scrollContainer.current;
  }

  private updateViewportVars(): void {
    // Keep the tab bar physically pinned to the visible app width. Some Android
    // browsers expose a layout viewport wider than documentElement.clientWidth;
    // using 100vw then creates horizontal drift and makes Home look unfixed.
    const visualViewport = window.visualViewport;
    const viewportWidth = Math.round(visualViewport?.width || document.documentElement.clientWidth || window.innerWidth);
    const visibleViewportBottom = (visualViewport?.offsetTop || 0) + (visualViewport?.height || document.documentElement.clientHeight || window.innerHeight);
    const bottomOffset = Math.max(0, Math.round(window.innerHeight - visibleViewportBottom));
    const shellWidth = Math.min(viewportWidth, 480);
    const shellLeft = Math.max(0, Math.round((viewportWidth - shellWidth) / 2));
    document.documentElement.style.setProperty('--visual-bottom-offset', bottomOffset + 'px');
    document.documentElement.style.setProperty('--app-visible-height', Math.round(visualViewport?.height || document.documentElement.clientHeight || window.innerHeight) + 'px');
    document.documentElement.style.setProperty('--app-shell-width', shellWidth + 'px');
    document.documentElement.style.setProperty('--app-shell-left', shellLeft + 'px');
  }

  private setupBrowserBackGuard(): void {
    if (navigator.userAgent.includes('jsdom')) return;
    try {
      window.history.replaceState({ quickfit: true }, '', window.location.href);
      window.history.pushState({ quickfitGuard: true }, '', window.location.href);
    } catch {
      // Browser history support is optional.
    }
  }

  private readonly handlePopState = (): void => {
    if (this.handlingPopState) return;
    if (this.isMainTabScreen(this.screen)) {
      window.removeEventListener('popstate', this.handlePopState);
      window.history.go(-Math.max(1, this.browserBackDepth + 1));
      return;
    }
    this.handlingPopState = true;
    if (this.browserBackDepth > 0) this.browserBackDepth -= 1;
    this.goBackWithinApp();
    this.handlingPopState = false;
  };

  private goBackWithinApp(): void {
    const previous = this.screenBackStack.pop();
    const target = previous || (this.screen === 'select' ? 'home' : 'home');
    this.goToScreen(target, target === 'home' ? 'Home dashboard.' : undefined, false);
  }

  private isMainTabScreen(screen: Screen): boolean {
    return screen === 'home' || screen === 'tasks' || screen === 'rank' || screen === 'me';
  }

  private chooseExercise(exerciseId: ExerciseId, target: ExerciseTarget = exerciseSpecs[exerciseId].defaultTarget): void {
    const pushedBack = this.pushBackScreen();
    if (pushedBack) this.pushBrowserBackEntry();
    this.setExerciseTarget(exerciseId, target);
    this.screen = 'preflight';
    this.statusText = `${this.currentSpec().shortTitle} battle is ready.`;
    void preloadImages(BATTLE_ASSETS);
    this.render();
  }

  private chooseJumpRope(target: JumpTarget = 100): void {
    this.chooseExercise('jump_rope', target);
  }

  private chooseReminderWorkout(): void {
    const spec = exerciseSpecs[this.reminderExerciseId];
    this.chooseExercise(spec.id, spec.defaultTarget);
  }

  private redrawReminderWorkout(announce = true): void {
    const options = exerciseIds.filter((id) => id !== this.reminderExerciseId);
    const pool = options.length ? options : exerciseIds;
    this.reminderExerciseId = pool[Math.floor(Math.random() * pool.length)] || 'jump_rope';
    const spec = exerciseSpecs[this.reminderExerciseId];
    this.setExerciseTarget(spec.id, spec.defaultTarget);
    if (announce) {
      this.statusText = `Workout card redrawn: ${spec.shortTitle}.`;
      this.render();
    }
  }

  private pushBackScreen(): boolean {
    if (this.screenBackStack[this.screenBackStack.length - 1] !== this.screen) {
      this.screenBackStack.push(this.screen);
      if (this.screenBackStack.length > 12) this.screenBackStack.shift();
      return true;
    }
    return false;
  }

  private pushBrowserBackEntry(): void {
    if (navigator.userAgent.includes('jsdom') || this.handlingPopState) return;
    try {
      window.history.pushState({ quickfitGuard: true }, '', window.location.href);
      this.browserBackDepth += 1;
    } catch {
      // Browser history support is optional.
    }
  }

  private goToScreen(screen: Screen, message?: string, pushBack = true, forcePushBack = false): void {
    this.stopMotion();
    this.clearTimers();
    void this.releaseWakeLock();
    if (this.screen === 'battle' || this.screen === 'paused') this.resetCounterState();
    const pushedBack = pushBack && screen !== this.screen && (forcePushBack || !this.isMainTabScreen(screen)) ? this.pushBackScreen() : false;
    if (pushedBack) this.pushBrowserBackEntry();
    this.screen = screen;
    if (message) this.statusText = message;
    if (screen === 'rank') this.refreshRankLeaderboards();
    this.render();
  }

  private goHome(): void {
    this.screenBackStack = [];
    this.browserBackDepth = 0;
    this.goToScreen('home', 'Home dashboard. Review movement progress, rewards, and profile.', false);
  }

  private goMainTab(screen: 'home' | 'tasks' | 'rank' | 'me', message: string): void {
    this.screenBackStack = [];
    this.browserBackDepth = 0;
    this.goToScreen(screen, message, false);
  }

  private setHapticEvery(value: HapticEvery): void {
    this.config = { ...this.config, hapticEvery: value };
    this.statusText = value === 0 ? 'Sound and vibration feedback are off.' : 'Very light rep vibration is on. Counter attacks use strong warnings.';
    this.persistProgress();
    this.render();
  }

  private setOutfit(value: Outfit): void {
    if (!this.ownedOutfits.includes(value)) {
      const price = outfitPrices[value];
      if (this.coins < price) {
        this.statusText = `${outfitLabels[value]} costs ${price} coins. You need ${price - this.coins} more.`;
        this.render();
        return;
      }
      this.coins -= price;
      this.ownedOutfits = [...this.ownedOutfits, value];
      this.statusText = `${outfitLabels[value]} unlocked for ${price} coins.`;
    } else {
      this.statusText = `${outfitLabels[value]} equipped.`;
    }
    this.outfit = value;
    this.persistProgress();
    void this.syncLeaderboard();
    this.render();
  }

  private saveName(): void {
    const input = this.root.querySelector<HTMLInputElement>('[data-name-input]');
    const nextName = input?.value.trim().slice(0, 24);
    if (!nextName) {
      this.statusText = 'Please enter a display name.';
      this.render();
      return;
    }
    this.playerName = nextName;
    this.statusText = `Name updated to ${this.playerName}.`;
    this.persistProgress();
    void this.syncLeaderboard();
    this.render();
  }

  private saveProfile(): void {
    const name = this.root.querySelector<HTMLInputElement>('[data-name-input]')?.value.trim().slice(0, 24);
    if (name) this.playerName = name;
    const gender = this.root.querySelector<HTMLSelectElement>('[data-profile-gender]')?.value as Gender | undefined;
    if (gender && ['unset', 'female', 'male', 'nonbinary'].includes(gender)) this.gender = gender;
    const birthDate = this.root.querySelector<HTMLInputElement>('[data-profile-birth]')?.value || '';
    this.birthDate = /^\d{4}-\d{2}-\d{2}$/.test(birthDate) ? birthDate : '';
    const height = Number(this.root.querySelector<HTMLInputElement>('[data-profile-height]')?.value);
    if (Number.isFinite(height) && height >= 80 && height <= 240) this.heightCm = Math.round(height);
    const weight = Number(this.root.querySelector<HTMLInputElement>('[data-profile-weight]')?.value);
    if (Number.isFinite(weight) && weight >= 25 && weight <= 250) this.weightKg = Math.round(weight);
    this.statusText = 'Profile saved.';
    this.persistProgress();
    void this.syncLeaderboard();
    this.render();
  }

  private localLeaderboardPlayer(): LeaderboardPlayer {
    return {
      playerId: this.playerId,
      name: this.playerName,
      outfit: this.outfit,
      level: this.playerLevel(),
      todayXp: this.dailyXp,
      monthXp: this.monthlyXp,
      totalXp: this.totalXp,
      todayDamage: this.dailyDamage,
      monthDamage: this.monthlyDamage,
      totalDamage: this.totalDamage,
      todayCalories: this.dailyCalories,
      monthCalories: this.monthlyCalories,
      totalCalories: this.totalCalories,
      todayBreaks: this.sitBreaksCompleted,
      monthBreaks: this.monthlySitBreaks,
      breaks: this.totalSitBreaks,
      streak: this.currentStreakDays(),
      workouts: this.workoutsCompleted
    };
  }

  private async syncLeaderboard(): Promise<void> {
    if (!this.playerId) return;
    const localPlayer = this.localLeaderboardPlayer();
    this.mergeLeaderboardPlayers([localPlayer]);
    try {
      const response = await fetch('/api/leaderboard', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ ...localPlayer, lastActiveDate: this.dateKey() })
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data = await response.json() as { players?: LeaderboardPlayer[] };
      this.mergeLeaderboardPlayers(data.players ?? []);    } catch {      this.mergeLeaderboardPlayers(this.sampleLeaderboardPlayers());
    }
    if (this.screen === 'rank') this.queueRender();
  }

  private async loadLeaderboard(scope: TimedLeaderboardScope = this.leaderboardScope): Promise<void> {
    this.leaderboardScope = scope;
    try {
      const response = await fetch(`/api/leaderboard?scope=${scope}`);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data = await response.json() as { players?: LeaderboardPlayer[] };
      this.mergeLeaderboardPlayers([this.localLeaderboardPlayer(), ...(data.players ?? [])]);    } catch {      this.mergeLeaderboardPlayers([this.localLeaderboardPlayer(), ...this.sampleLeaderboardPlayers()]);
    }
    if (this.screen === 'rank') this.render();
  }

  private async loadBreakLeaderboard(scope: TimedLeaderboardScope = this.breakLeaderboardScope): Promise<void> {
    this.breakLeaderboardScope = scope;
    try {
      const response = await fetch(`/api/leaderboard?scope=break_${scope}`);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data = await response.json() as { players?: LeaderboardPlayer[] };
      this.mergeLeaderboardPlayers([this.localLeaderboardPlayer(), ...(data.players ?? [])]);    } catch {      this.mergeLeaderboardPlayers([this.localLeaderboardPlayer(), ...this.sampleLeaderboardPlayers()]);
    }
    if (this.screen === 'rank') this.render();
  }

  private async loadXpLeaderboard(scope: TimedLeaderboardScope = this.xpLeaderboardScope): Promise<void> {
    this.xpLeaderboardScope = scope;
    try {
      const apiScope = scope === 'total' ? 'xp_total' : `xp_${scope}`;
      const response = await fetch(`/api/leaderboard?scope=${apiScope}`);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data = await response.json() as { players?: LeaderboardPlayer[] };
      this.mergeLeaderboardPlayers([this.localLeaderboardPlayer(), ...(data.players ?? [])]);
    } catch {
      this.mergeLeaderboardPlayers([this.localLeaderboardPlayer(), ...this.sampleLeaderboardPlayers()]);
    }
    if (this.screen === 'rank') this.render();
  }

  private async loadDamageLeaderboard(scope: TimedLeaderboardScope = this.damageLeaderboardScope): Promise<void> {
    this.damageLeaderboardScope = scope;
    try {
      const apiScope = scope === 'today' ? 'damage_today' : scope === 'month' ? 'damage_month' : 'damage_total';
      const response = await fetch(`/api/leaderboard?scope=${apiScope}`);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data = await response.json() as { players?: LeaderboardPlayer[] };
      this.mergeLeaderboardPlayers([this.localLeaderboardPlayer(), ...(data.players ?? [])]);
    } catch {
      this.mergeLeaderboardPlayers([this.localLeaderboardPlayer(), ...this.sampleLeaderboardPlayers()]);
    }
    if (this.screen === 'rank') this.render();
  }

  private refreshRankLeaderboards(): void {
    void this.syncLeaderboard();
    void this.loadXpLeaderboard(this.xpLeaderboardScope);
    void this.loadDamageLeaderboard(this.damageLeaderboardScope);
    void this.loadBreakLeaderboard(this.breakLeaderboardScope);
    void this.loadLeaderboard(this.leaderboardScope);
  }

  private mergeLeaderboardPlayers(players: LeaderboardPlayer[]): void {
    const byId = new Map<string, LeaderboardPlayer>();
    [...this.leaderboardPlayers, ...players].forEach((player) => {
      if (!player?.playerId) return;
      byId.set(player.playerId, this.normalizeLeaderboardPlayer({ ...byId.get(player.playerId), ...player }));
    });
    this.leaderboardPlayers = [...byId.values()];
  }

  private sampleLeaderboardPlayers(): LeaderboardPlayer[] {
    return [
      { playerId: 'sample-luna', name: 'LunaDesk', outfit: 'sunny', level: 42, todayXp: 360, monthXp: 3420, totalXp: 4100, todayDamage: 840, monthDamage: 8400, totalDamage: 26400, todayCalories: 92, monthCalories: 820, totalCalories: 4200, todayBreaks: 3, monthBreaks: 31, breaks: 118, streak: 8, workouts: 42 },
      { playerId: 'sample-max', name: 'MaxMove', outfit: 'legendary', level: 37, todayXp: 280, monthXp: 2890, totalXp: 3600, todayDamage: 690, monthDamage: 6900, totalDamage: 21200, todayCalories: 74, monthCalories: 690, totalCalories: 3900, todayBreaks: 2, monthBreaks: 26, breaks: 97, streak: 5, workouts: 33 },
      { playerId: 'sample-ava', name: 'AvaJump', outfit: 'coral', level: 28, todayXp: 190, monthXp: 2140, totalXp: 2700, todayDamage: 460, monthDamage: 4600, totalDamage: 15700, todayCalories: 48, monthCalories: 540, totalCalories: 2600, todayBreaks: 1, monthBreaks: 22, breaks: 76, streak: 4, workouts: 29 }
    ];
  }

  private normalizeLeaderboardPlayer(player: LeaderboardPlayer): LeaderboardPlayer {
    const breaks = this.cleanLeaderboardNumber(player.breaks);
    return {
      ...player,
      outfit: this.isKnownOutfit(player.outfit) ? player.outfit : 'teal',
      level: this.cleanLeaderboardNumber(player.level) || this.levelFromTotalXp(this.cleanLeaderboardNumber(player.totalXp)),
      todayXp: this.cleanLeaderboardNumber(player.todayXp),
      monthXp: this.cleanLeaderboardNumber(player.monthXp),
      totalXp: this.cleanLeaderboardNumber(player.totalXp),
      todayDamage: this.cleanLeaderboardNumber(player.todayDamage),
      monthDamage: this.cleanLeaderboardNumber(player.monthDamage),
      totalDamage: this.cleanLeaderboardNumber(player.totalDamage),
      todayBreaks: this.cleanLeaderboardNumber(player.todayBreaks),
      monthBreaks: this.cleanLeaderboardNumber(player.monthBreaks),
      breaks
    };
  }

  private cleanLeaderboardNumber(value: unknown): number {
    const number = Number(value);
    return Number.isFinite(number) && number > 0 ? Math.round(number) : 0;
  }

  private formatSittingElapsed(): string {
    const totalMinutes = Math.floor(this.sittingElapsedMs / 60_000);
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    if (hours > 0) return `${hours}h ${minutes.toString().padStart(2, '0')}m`;
    return `${minutes}m`;
  }

  private sittingUrgency(): 'calm' | 'warning' | 'urgent' {
    if (this.sittingElapsedMs >= this.reminderIntervalMinutes * 1.5 * 60_000) return 'urgent';
    if (this.sittingElapsedMs >= this.reminderIntervalMinutes * 60_000) return 'warning';
    return 'calm';
  }

  private minutesFromClock(value: string): number | null {
    const match = /^(\d{2}):(\d{2})$/.exec(value);
    if (!match) return null;
    const hours = Number(match[1]);
    const minutes = Number(match[2]);
    if (hours > 23 || minutes > 59) return null;
    return hours * 60 + minutes;
  }

  private dndSegments(window: DndWindow): Array<[number, number]> {
    const start = this.minutesFromClock(window.start);
    const end = this.minutesFromClock(window.end);
    if (start === null || end === null || start === end) return [];
    return start < end ? [[start, end]] : [[start, 1440], [0, end]];
  }

  private sanitizeDndWindows(value: unknown[]): DndWindow[] {
    const windows: DndWindow[] = [];
    for (const item of value) {
      if (!item || typeof item !== 'object') continue;
      const candidate = item as Partial<DndWindow>;
      if (typeof candidate.start !== 'string' || typeof candidate.end !== 'string') continue;
      if (this.dndSegments({ start: candidate.start, end: candidate.end }).length) windows.push({ start: candidate.start, end: candidate.end });
    }
    return windows;
  }

  private dndOverlaps(candidate: DndWindow, existing = this.dndWindows): boolean {
    const candidateSegments = this.dndSegments(candidate);
    return candidateSegments.some(([startA, endA]) =>
      existing.flatMap((item) => this.dndSegments(item)).some(([startB, endB]) => startA < endB && startB < endA)
    );
  }

  private isInDoNotDisturb(date = new Date()): boolean {
    const minute = date.getHours() * 60 + date.getMinutes();
    return this.dndWindows.some((window) => this.dndSegments(window).some(([start, end]) => minute >= start && minute < end));
  }

  private ageLabel(): string {
    if (!this.birthDate) return 'Not set';
    const birth = new Date(`${this.birthDate}T00:00:00`);
    if (Number.isNaN(birth.getTime())) return 'Not set';
    const today = new Date();
    let age = today.getFullYear() - birth.getFullYear();
    const hadBirthday = today.getMonth() > birth.getMonth() || (today.getMonth() === birth.getMonth() && today.getDate() >= birth.getDate());
    if (!hadBirthday) age -= 1;
    return age >= 0 && age <= 120 ? `${age}` : 'Not set';
  }

  private dateKey(date = new Date()): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private monthKey(date = new Date()): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    return `${year}-${month}`;
  }

  private createPlayerId(): string {
    const cryptoId = window.crypto?.randomUUID?.();
    return cryptoId || `player-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
  }

  private resetDailyCaloriesIfNeeded(): void {
    const today = this.dateKey();
    const thisMonth = this.monthKey();
    let changed = false;
    if (this.dailyCaloriesDate !== today) {
      this.dailyCaloriesDate = today;
      this.dailyCalories = 0;
      this.dailyXp = 0;
      this.dailyDamage = 0;
      this.sitBreaksCompleted = 0;
      this.rewardClaimed = false;
      this.wellnessTipRead = false;
      changed = true;
    }
    if (this.monthlyCaloriesKey !== thisMonth) {
      this.monthlyCaloriesKey = thisMonth;
      this.monthlyCalories = 0;
      changed = true;
    }
    if (this.monthlyXpKey !== thisMonth) {
      this.monthlyXpKey = thisMonth;
      this.monthlyXp = 0;
      changed = true;
    }
    if (this.monthlyDamageKey !== thisMonth) {
      this.monthlyDamageKey = thisMonth;
      this.monthlyDamage = 0;
      changed = true;
    }
    if (this.monthlySitBreaksKey !== thisMonth) {
      this.monthlySitBreaksKey = thisMonth;
      this.monthlySitBreaks = 0;
      changed = true;
    }
    if (changed) this.persistProgress();
  }

  private currentSpec(): typeof exerciseSpecs[ExerciseId] {
    return exerciseSpecs[this.currentExerciseId];
  }

  private estimateCalories(reps: number, elapsedMs?: number, exerciseId = this.currentExerciseId): number {
    const spec = exerciseSpecs[exerciseId];
    const safeReps = Math.max(0, reps);
    const estimatedHours = safeReps / spec.rpm / 60;
    const measuredHours = elapsedMs && elapsedMs > 0 ? elapsedMs / 3_600_000 : 0;
    const hours = Math.max(estimatedHours, measuredHours);
    return Math.max(0, Math.round(spec.met * assumedBodyWeightKg * hours));
  }

  private estimatedCaloriesForTarget(target: ExerciseTarget = this.config.targetJumps as ExerciseTarget, exerciseId = this.currentExerciseId): number {
    return this.estimateCalories(target, undefined, exerciseId);
  }

  private estimatedRunningDistanceMeters(reps: number): number {
    return Math.round(Math.max(0, reps) * runningMetersPerRep);
  }

  private playerLevel(totalXp = this.totalXp): number {
    return this.levelFromTotalXp(totalXp);
  }

  private levelFromTotalXp(totalXp: number): number {
    let level = 1;
    let remaining = Math.max(0, Math.floor(totalXp));
    while (remaining >= this.xpRequiredForLevel(level)) {
      remaining -= this.xpRequiredForLevel(level);
      level += 1;
    }
    return level;
  }

  private xpRequiredForLevel(level: number): number {
    return Math.max(1, Math.round(baseXpPerLevel * Math.pow(xpGrowthPerLevel, Math.max(0, level - 1))));
  }

  private totalXpRequiredForLevel(level: number): number {
    let total = 0;
    for (let current = 1; current < Math.max(1, level); current += 1) total += this.xpRequiredForLevel(current);
    return total;
  }

  private xpIntoCurrentLevel(totalXp = this.totalXp): number {
    return Math.max(0, Math.floor(totalXp) - this.totalXpRequiredForLevel(this.levelFromTotalXp(totalXp)));
  }

  private xpToNextLevel(totalXp = this.totalXp): number {
    const level = this.levelFromTotalXp(totalXp);
    return Math.max(0, this.xpRequiredForLevel(level) - this.xpIntoCurrentLevel(totalXp));
  }

  private xpProgressPercent(): number {
    const level = this.playerLevel();
    return Math.max(0, Math.min(100, (this.xpIntoCurrentLevel() / this.xpRequiredForLevel(level)) * 100));
  }

  private experienceForRound(result: GameResult): number {
    const dodgeMultiplier = 1 + this.successfulDodges * DEFAULT_COUNTER_ATTACK_CONFIG.dodgeRewardBonusRate;
    return Math.max(10, Math.round(result.jumpCount * dodgeMultiplier));
  }

  private damageDealtForRound(): number {
    const damagePerRep = this.combat.enemyMaxHp / this.combat.targetJumps;
    return Math.max(0, Math.round(this.combat.jumpCount * damagePerRep));
  }

  private formatDistance(meters: number): string {
    if (meters >= 1000) return `${(meters / 1000).toFixed(2)} km`;
    return `${meters} m`;
  }

  private setExerciseTarget(exerciseId: ExerciseId, target: ExerciseTarget): void {
    this.currentExerciseId = exerciseId;
    this.config = this.configForTarget(target);
    this.combat = createCombatState(this.config);
    this.statusText = `${this.currentSpec().shortTitle} target set to ${target} ${this.currentSpec().countLabel}.`;
    this.persistProgress();
  }

  private configForTarget(target: number): GameConfig {
    const playerBaseAttack = this.playerBaseAttack();
    return {
      ...this.config,
      targetJumps: target,
      playerBaseAttack,
      enemyMaxHp: this.monsterMaxHpForTarget(target)
    };
  }

  private playerBaseAttack(): number {
    return 1;
  }

  private monsterMaxHpForTarget(target: number): number {
    const baseHp = Math.max(20, Math.round(target / 5));
    return baseHp * this.playerBaseAttack();
  }

  private resetCounterState(): void {
    this.battlePhase = 'ACTIVE_ATTACK';
    this.counterCountdown = 0;
    this.counterDirection = null;
    this.dodgeWindowOpenedAt = 0;
    this.pendingGraceDodgeDirection = null;
    this.pendingGraceDodgeStrength = 0;
    this.dodgeBaseline = null;
    this.lastCounterHpBeforeHeal = 0;
    this.lastCounterHpAfterHeal = 0;
    this.successfulDodges = 0;
    this.counterTriggers = [];
    if (this.counterTimer) {
      clearTimeout(this.counterTimer);
      this.counterTimer = null;
    }
  }

  private escapeHtml(value: string): string {
    return value
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#39;');
  }

  private addDays(date: Date, days: number): Date {
    const next = new Date(date);
    next.setDate(next.getDate() + days);
    return next;
  }

  private recordStreakCheckIn(date = new Date()): void {
    const key = this.dateKey(date);
    if (!this.streakDates.includes(key)) {
      this.streakDates = [...this.streakDates, key].sort().slice(-120);
    }
  }

  private currentStreakDays(): number {
    const checked = new Set(this.streakDates);
    let cursor = new Date();
    if (!checked.has(this.dateKey(cursor))) cursor = this.addDays(cursor, -1);
    let days = 0;
    while (checked.has(this.dateKey(cursor))) {
      days += 1;
      cursor = this.addDays(cursor, -1);
    }
    return days;
  }

  private renderSittingTimer(): string {
    const urgency = this.sittingUrgency();
    const dnd = this.isInDoNotDisturb();
    const nextBreakMs = this.reminderIntervalMinutes * 60_000 - (this.sittingElapsedMs % (this.reminderIntervalMinutes * 60_000));
    const nextBreakMinutes = Math.max(1, Math.ceil(nextBreakMs / 60_000));
    const permission = 'Notification' in window ? Notification.permission : 'unsupported';
    const stateCopy = dnd ? 'Quiet hours active' : urgency === 'urgent' ? 'Move now!' : urgency === 'warning' ? 'Break overdue' : 'Ready for a draw';
    const detailCopy = dnd ? 'Sit timer is paused by do-not-disturb.' : `Next card in ${nextBreakMinutes}m`;
    const meterValue = dnd ? 'Paused' : this.formatSittingElapsed();
    const permissionControl = permission === 'granted'
      ? '<button class="timer-permission on" data-action="enable-notifications" aria-pressed="true">Alerts on</button>'
      : permission === 'unsupported'
        ? '<span class="timer-chip muted">In-app timer</span>'
        : '<button class="timer-permission" data-action="enable-notifications" aria-pressed="false">Enable alerts</button>';
    return `<div class="sitting-timer urgency-${urgency} ${dnd ? 'is-dnd' : ''}"><div class="timer-orb"><small>${dnd ? 'Shield' : 'Timer'}</small><strong>${meterValue}</strong></div><div class="timer-copy"><span>Sit-break meter</span><em>${stateCopy}</em><i>${detailCopy}</i></div>${permissionControl}</div>`;
  }

  private readWellnessTip(): void {
    this.wellnessTipRead = true;
    this.statusText = 'Wellness tip read: stand tall, relax your shoulders, and breathe steadily.';
    this.persistProgress();
    this.render();
  }

  private saveReminderSettings(): void {
    const interval = Number(this.root.querySelector<HTMLInputElement>('[data-reminder-interval]')?.value);
    if (!Number.isFinite(interval) || interval < 10 || interval > 120) {
      this.statusText = 'Reminder interval must be 10-120 minutes.';
      this.render();
      return;
    }
    this.reminderIntervalMinutes = Math.round(interval);
    this.updateSittingElapsed();
    this.reminderBucketSent = Math.floor(this.sittingElapsedMs / (this.reminderIntervalMinutes * 60_000));
    this.statusText = `Saved. Next reminder uses ${this.reminderIntervalMinutes} minutes; your current sitting timer was kept.`;
    this.persistProgress();
    this.syncBackgroundReminderIfEnabled();
    this.render();
  }

  private addDndWindow(): void {
    const start = this.root.querySelector<HTMLInputElement>('[data-dnd-start-new]')?.value || '';
    const end = this.root.querySelector<HTMLInputElement>('[data-dnd-end-new]')?.value || '';
    const candidate = { start, end };
    if (!this.dndSegments(candidate).length) {
      this.statusText = 'Choose a valid do-not-disturb time range.';
      this.render();
      return;
    }
    if (this.dndOverlaps(candidate)) {
      this.statusText = 'That do-not-disturb range overlaps an existing range, so it was not added.';
      this.render();
      return;
    }
    this.dndWindows = [...this.dndWindows, candidate].sort((a, b) => (this.minutesFromClock(a.start) ?? 0) - (this.minutesFromClock(b.start) ?? 0));
    this.statusText = `Do-not-disturb added: ${start}-${end}.`;
    this.persistProgress();
    this.syncBackgroundReminderIfEnabled();
    this.render();
  }

  private removeDndWindow(index: number): void {
    this.dndWindows = this.dndWindows.filter((_, itemIndex) => itemIndex !== index);
    if (!this.dndWindows.length) this.dndWindows = [{ start: '21:00', end: '09:00' }];
    this.statusText = 'Do-not-disturb range removed.';
    this.persistProgress();
    this.syncBackgroundReminderIfEnabled();
    this.render();
  }

  private resetLocalProgress(): void {
    this.coins = 0;
    this.sitBreaksCompleted = 0;
    this.totalSitBreaks = 0;
    this.workoutsCompleted = 0;
    this.streakDates = [];
    this.dailyCalories = 0;
    this.monthlyCalories = 0;
    this.totalCalories = 0;
    this.lastCaloriesEarned = 0;
    this.dailyXp = 0;
    this.monthlyXp = 0;
    this.totalXp = 0;
    this.lastXpEarned = 0;
    this.dailyDamage = 0;
    this.monthlyDamage = 0;
    this.totalDamage = 0;
    this.lastDamageDealt = 0;
    this.lastLevelBeforeRound = 1;
    this.lastLevelAfterRound = 1;
    this.rewardClaimed = false;
    this.wellnessTipRead = false;
    this.statusText = 'Local progress reset on this device.';
    this.persistProgress();
    void this.syncLeaderboard();
    this.render();
  }

  private handleLockedExercise(title: string): void {
    this.statusText = `${title} is coming soon. Jump Rope is available today.`;
    this.render();
  }

  private async beginMotionFlow(): Promise<void> {
    this.screen = 'calibrating';
    this.statusText = 'Starting challenge and requesting motion permission.';
    this.render();
    const permission = await requestMotionPermission();
    if (permission !== 'granted') {
      this.startTapFallback(permission === 'denied' ? 'Motion permission was denied, so tap testing is now active.' : 'This browser cannot read motion sensors, so tap testing is now active.');
      return;
    }
    this.detector = new JumpDetector({ ...this.currentSpec().detector, minValidIntervalMs: this.currentSpec().detector.minValidIntervalMs, maxValidIntervalMs: this.currentSpec().detector.maxValidIntervalMs });
    this.motionStream = new MotionStream(this.handleMotionSample);
    this.motionStream.start();
    this.statusText = `Hold your phone steady for 1 second to calibrate, then start ${this.currentSpec().shortTitle.toLowerCase()}.`;
    this.render();
    this.calibrationTimer = setTimeout(() => {
      if (this.screen === 'calibrating' && !this.detector?.isCalibrated) {
        this.startTapFallback('No motion data arrived, so tap testing is now active.');
      }
    }, 2400);
  }

  private readonly handleMotionSample = (sample: MotionSample): void => {
    if (!this.detector) return;
    if (this.screen === 'calibrating') {
      const event = this.detector.process(sample);
      if (event.status === 'ready') this.startBattle('motion', `Calibration complete. Start ${this.currentSpec().shortTitle.toLowerCase()}!`);
      return;
    }
    if (this.screen !== 'battle' || this.sensorMode !== 'motion') return;
    this.refreshSensorStaleWatch();
    if (this.battlePhase === 'COUNTER_COUNTDOWN') {
      this.updateDodgeBaseline(sample);
      return;
    }
    if (this.battlePhase === 'DODGE_WINDOW') {
      this.tryResolveDodgeFromMotion(sample);
      return;
    }
    if (this.battlePhase !== 'ACTIVE_ATTACK') return;
    const event = this.detector.process(sample);
    if (event.status === 'jump' || event.status === 'gyro_assisted_jump') {
      this.registerJump(event.status);
    } else if (event.status === 'rejected_spin') {
      this.handleSpinRejection();
    }
  };

  private startBattle(mode: SensorMode, message: string): void {
    this.clearTimers(['hit', 'stale', 'victory', 'counter']);
    this.sensorMode = mode;
    this.config = this.configForTarget(this.config.targetJumps);
    this.combat = createCombatState(this.config);
    this.result = null;
    this.enemyPose = 'idle';
    this.resetCounterState();
    this.counterTriggers = this.createCounterTriggers(this.combat.enemyMaxHp);
    this.clearSpinRejections();
    this.timer.start();
    this.screen = 'battle';
    this.statusText = message;
    if (mode === 'motion') this.refreshSensorStaleWatch();
    void this.requestWakeLock();
    this.render();
  }

  private createCounterTriggers(maxHp: number): CounterTrigger[] {
    const first = this.randomInRange(DEFAULT_COUNTER_ATTACK_CONFIG.firstThresholdRange) * maxHp;
    const second = this.randomInRange(DEFAULT_COUNTER_ATTACK_CONFIG.secondThresholdRange) * maxHp;
    return [
      { id: 'first', thresholdHp: first, consumed: false },
      { id: 'second', thresholdHp: second, consumed: false }
    ];
  }

  private randomInRange([min, max]: [number, number]): number {
    return min + Math.random() * (max - min);
  }

  private startTapFallback(message = `Tap testing is active: each tap counts as one ${this.currentSpec().countLabel.slice(0, -1) || 'rep'}.`): void {
    this.stopMotion();
    this.detector = null;
    this.startBattle('tap_fallback', message);
  }
  private registerJump(kind: 'jump' | 'gyro_assisted_jump' = 'jump'): void {
    if (this.combat.defeated || this.screen !== 'battle') return;
    if (this.battlePhase === 'DODGE_WINDOW' && this.sensorMode === 'tap_fallback') {
      this.resolveDodge(true);
      return;
    }
    if (this.battlePhase !== 'ACTIVE_ATTACK') return;
    const spec = this.currentSpec();
    this.clearSpinRejections();
    this.combat = applyJumpDamage(this.combat);
    const vibrated = maybeVibrateForJump(this.combat.jumpCount, this.config.hapticEvery);
    const milestoneEvery = Math.max(1, Math.round(this.config.targetJumps * 0.1));
    const milestone = this.combat.jumpCount % milestoneEvery === 0 || this.combat.defeated;
    if (milestone) {
      this.playHitSound(true);
      this.ultimateActive = true;
      if (this.ultimateTimer) clearTimeout(this.ultimateTimer);
      this.ultimateTimer = setTimeout(() => { this.ultimateActive = false; this.queueRender(); }, 520);
    }
    this.enemyPose = this.combat.defeated ? 'defeated' : 'hit';
    const hpCopy = `${Math.ceil(this.combat.enemyHp)}/${this.combat.enemyMaxHp} HP`;
    this.statusText = kind === 'gyro_assisted_jump'
      ? `Hit confirmed by gyroscope assist. Monster ${hpCopy}.`
      : milestone ? `Power strike! Monster ${hpCopy}.` : vibrated ? `Hit landed. Monster ${hpCopy}.` : `Hit landed. Keep the rhythm. Monster ${hpCopy}.`;
    if (this.hitTimer) clearTimeout(this.hitTimer);
    if (!this.combat.defeated) {
      this.hitTimer = setTimeout(() => { this.enemyPose = 'idle'; this.queueRender(); }, 180);
    }
    if (this.combat.defeated) {
      this.statusText = `${spec.shortTitle} complete! Desk Monster defeated.`;
      vibrateForVictory(this.config.hapticEvery !== 0);
      if (this.config.hapticEvery !== 0) this.playVictorySound();
      this.victoryTimer = setTimeout(() => this.finishRound(), 760);
    } else {
      this.maybeTriggerCounterAttack();
    }
    this.queueRender();
  }

  private maybeTriggerCounterAttack(): void {
    if (this.battlePhase !== 'ACTIVE_ATTACK') return;
    const trigger = this.counterTriggers.find((item) => !item.consumed && this.combat.enemyHp <= item.thresholdHp);
    if (!trigger) return;
    trigger.consumed = true;
    this.startCounterCountdown(trigger.id);
  }

  private startCounterCountdown(id: CounterTrigger['id']): void {
    if (this.hitTimer) {
      clearTimeout(this.hitTimer);
      this.hitTimer = null;
    }
    this.enemyPose = 'idle';
    this.battlePhase = 'COUNTER_COUNTDOWN';
    this.counterCountdown = 3;
    this.counterDirection = null;
    this.dodgeBaseline = null;
    this.statusText = `${id === 'first' ? 'First' : 'Second'} counter attack incoming. Stop jumping and prepare to dodge.`;
    vibrateForCounterCountdown(3, this.config.hapticEvery !== 0);
    if (this.config.hapticEvery !== 0) this.playCounterChargeSound();
    this.queueRender();
    this.counterTimer = setTimeout(() => this.advanceCounterCountdown(), DEFAULT_COUNTER_ATTACK_CONFIG.countdownStepMs);
  }

  private advanceCounterCountdown(): void {
    if (this.screen !== 'battle' || this.battlePhase !== 'COUNTER_COUNTDOWN') return;
    if (this.counterCountdown === 3) {
      this.counterCountdown = 2;
      this.statusText = 'Counter charging. Stay ready.';
      vibrateForCounterCountdown(2, this.config.hapticEvery !== 0);
      if (this.config.hapticEvery !== 0) this.playCounterChargeSound();
      this.queueRender();
      this.counterTimer = setTimeout(() => this.advanceCounterCountdown(), DEFAULT_COUNTER_ATTACK_CONFIG.countdownStepMs);
      return;
    }
    this.enterDodgeWindow();
  }

  private enterDodgeWindow(): void {
    if (this.screen !== 'battle') return;
    this.battlePhase = 'DODGE_WINDOW';
    this.counterCountdown = 1;
    this.counterDirection = this.randomDodgeDirection();
    this.dodgeWindowOpenedAt = performance.now();
    this.pendingGraceDodgeDirection = null;
    this.pendingGraceDodgeStrength = 0;
    this.statusText = `Dodge ${this.counterDirection}! Move the phone fast.`;
    vibrateForDodgeWarning(this.config.hapticEvery !== 0);
    if (this.config.hapticEvery !== 0) this.playCounterStrikeSound();
    this.queueRender();
    this.counterTimer = setTimeout(() => this.resolveDodge(false), DEFAULT_COUNTER_ATTACK_CONFIG.dodgeWindowMs + dodgeInputGraceMs);
  }

  private tryResolveDodgeFromMotion(sample: MotionSample): void {
    if (!this.counterDirection) return;
    const sampleTime = Number.isFinite(sample.timestamp) ? sample.timestamp : performance.now();
    const elapsed = this.dodgeWindowOpenedAt > 0 ? sampleTime - this.dodgeWindowOpenedAt : dodgeInputGraceMs;
    if (elapsed < dodgeSensorSettleMs) return;
    const dodgeSample = this.relativeDodgeSample(sample);
    if (!dodgeSample) return;
    const event = this.dodgeDetector.process(dodgeSample);
    if (!event.direction) return;
    if (elapsed < dodgeInputGraceMs) {
      if (!this.pendingGraceDodgeDirection || event.strength > this.pendingGraceDodgeStrength) {
        this.pendingGraceDodgeDirection = event.direction;
        this.pendingGraceDodgeStrength = event.strength;
      }
      return;
    }
    const direction = this.pendingGraceDodgeDirection && this.pendingGraceDodgeStrength >= event.strength
      ? this.pendingGraceDodgeDirection
      : event.direction;
    this.resolveDodge(direction === this.counterDirection);
  }

  private updateDodgeBaseline(sample: MotionSample): void {
    const x = this.finiteAxis(sample.x);
    const y = this.finiteAxis(sample.y);
    const z = this.finiteAxis(sample.z);
    if (x === null || y === null || z === null) return;
    if (!this.dodgeBaseline) {
      this.dodgeBaseline = { x, y, z };
      return;
    }
    const alpha = 0.18;
    this.dodgeBaseline = {
      x: this.dodgeBaseline.x + (x - this.dodgeBaseline.x) * alpha,
      y: this.dodgeBaseline.y + (y - this.dodgeBaseline.y) * alpha,
      z: this.dodgeBaseline.z + (z - this.dodgeBaseline.z) * alpha
    };
  }

  private relativeDodgeSample(sample: MotionSample): MotionSample | null {
    if (!this.dodgeBaseline) {
      this.updateDodgeBaseline(sample);
      return null;
    }
    const x = this.finiteAxis(sample.x);
    const y = this.finiteAxis(sample.y);
    const z = this.finiteAxis(sample.z);
    if (x === null || y === null || z === null) return null;
    return {
      ...sample,
      x: x - this.dodgeBaseline.x,
      y: y - this.dodgeBaseline.y,
      z: z - this.dodgeBaseline.z
    };
  }

  private finiteAxis(value: number | null | undefined): number | null {
    return typeof value === 'number' && Number.isFinite(value) ? value : null;
  }

  private resolveDodge(success: boolean): void {
    if (this.screen !== 'battle' || this.battlePhase !== 'DODGE_WINDOW') return;
    if (this.counterTimer) {
      clearTimeout(this.counterTimer);
      this.counterTimer = null;
    }
    this.counterCountdown = 0;
    if (success) {
      this.successfulDodges += 1;
      this.lastCounterHpBeforeHeal = 0;
      this.lastCounterHpAfterHeal = 0;
      this.battlePhase = 'DODGE_SUCCESS';
      this.statusText = `Perfect Dodge! Reward bonus +${Math.round(this.successfulDodges * DEFAULT_COUNTER_ATTACK_CONFIG.dodgeRewardBonusRate * 100)}%.`;
      vibrateForDodgeSuccess(this.config.hapticEvery !== 0);
      if (this.config.hapticEvery !== 0) this.playDodgeResultSound(true);
    } else {
      this.battlePhase = 'DODGE_FAILED';
      const hpBeforeHeal = this.combat.enemyHp;
      this.lastCounterHpBeforeHeal = hpBeforeHeal;
      this.combat = healEnemy(this.combat, this.combat.enemyMaxHp * DEFAULT_COUNTER_ATTACK_CONFIG.monsterHealRate);
      this.lastCounterHpAfterHeal = this.combat.enemyHp;
      this.enemyPose = 'idle';
      this.statusText = 'Dodge missed. The monster healed and its HP bar pushed back.';
      vibrateForDodgeFailure(this.config.hapticEvery !== 0);
      if (this.config.hapticEvery !== 0) this.playDodgeResultSound(false);
    }
    this.queueRender();
    this.counterTimer = setTimeout(() => {
      if (this.screen !== 'battle') return;
      this.battlePhase = 'ACTIVE_ATTACK';
      this.counterDirection = null;
      this.pendingGraceDodgeDirection = null;
      this.pendingGraceDodgeStrength = 0;
      this.enemyPose = 'idle';
      this.statusText = success ? 'Back to attack rhythm.' : 'Recover and keep attacking.';
      this.queueRender();
    }, 1500);
  }

  private randomDodgeDirection(): DodgeDirection {
    const directions: DodgeDirection[] = ['UP', 'DOWN', 'LEFT', 'RIGHT'];
    const pool = directions.filter((direction) => direction !== this.lastCounterDirection);
    const next = pool[Math.floor(Math.random() * pool.length)] || 'UP';
    this.lastCounterDirection = next;
    return next;
  }

  private directionArrow(direction: DodgeDirection | null): string {
    const label = direction || 'UP';
    return `<svg viewBox="0 0 120 120" role="img" aria-label="${label} dodge"><path d="M60 12 104 58H78v42H42V58H16z"/></svg>`;
  }

  private playHitSound(heavy: boolean): void {
    try {
      const AudioContextCtor = window.AudioContext || (window as Window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
      if (!AudioContextCtor) return;
      const ctx = new AudioContextCtor();
      const now = ctx.currentTime;
      const gain = ctx.createGain();
      gain.connect(ctx.destination);
      gain.gain.setValueAtTime(0.0001, now);
      gain.gain.exponentialRampToValueAtTime(heavy ? 0.22 : 0.14, now + 0.015);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + (heavy ? 0.24 : 0.16));

      const osc = ctx.createOscillator();
      osc.type = 'triangle';
      osc.frequency.setValueAtTime(heavy ? 620 : 520, now);
      osc.frequency.exponentialRampToValueAtTime(heavy ? 160 : 240, now + (heavy ? 0.18 : 0.12));
      osc.connect(gain);
      osc.start(now);
      osc.stop(now + (heavy ? 0.24 : 0.16));

      const sparkle = ctx.createOscillator();
      sparkle.type = 'sine';
      sparkle.frequency.setValueAtTime(heavy ? 1240 : 980, now + 0.02);
      sparkle.frequency.exponentialRampToValueAtTime(heavy ? 1860 : 1420, now + 0.11);
      sparkle.connect(gain);
      sparkle.start(now + 0.02);
      sparkle.stop(now + 0.13);

      const noiseDuration = heavy ? 0.18 : 0.11;
      const noiseBuffer = ctx.createBuffer(1, Math.max(1, Math.floor(ctx.sampleRate * noiseDuration)), ctx.sampleRate);
      const samples = noiseBuffer.getChannelData(0);
      for (let index = 0; index < samples.length; index += 1) {
        const fade = 1 - index / samples.length;
        samples[index] = (Math.random() * 2 - 1) * fade * (heavy ? 0.42 : 0.24);
      }
      const noise = ctx.createBufferSource();
      noise.buffer = noiseBuffer;
      const noiseGain = ctx.createGain();
      noiseGain.gain.setValueAtTime(heavy ? 0.16 : 0.08, now);
      noiseGain.gain.exponentialRampToValueAtTime(0.0001, now + noiseDuration);
      noise.connect(noiseGain);
      noiseGain.connect(ctx.destination);
      noise.start(now);
      noise.stop(now + noiseDuration);

      setTimeout(() => void ctx.close(), 420);
    } catch {
      // Audio feedback is optional and may be blocked until a user gesture.
    }
  }

  private playCounterChargeSound(): void {
    try {
      const AudioContextCtor = window.AudioContext || (window as Window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
      if (!AudioContextCtor) return;
      const ctx = new AudioContextCtor();
      const now = ctx.currentTime;
      const gain = ctx.createGain();
      gain.connect(ctx.destination);
      gain.gain.setValueAtTime(0.0001, now);
      gain.gain.exponentialRampToValueAtTime(0.16, now + 0.03);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.55);
      const osc = ctx.createOscillator();
      osc.type = 'sawtooth';
      osc.frequency.setValueAtTime(92, now);
      osc.frequency.exponentialRampToValueAtTime(210, now + 0.48);
      osc.connect(gain);
      osc.start(now);
      osc.stop(now + 0.55);
      setTimeout(() => void ctx.close(), 720);
    } catch {
      // Optional audio.
    }
  }

  private playCounterStrikeSound(): void {
    try {
      const AudioContextCtor = window.AudioContext || (window as Window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
      if (!AudioContextCtor) return;
      const ctx = new AudioContextCtor();
      const now = ctx.currentTime;
      const gain = ctx.createGain();
      gain.connect(ctx.destination);
      gain.gain.setValueAtTime(0.0001, now);
      gain.gain.exponentialRampToValueAtTime(0.22, now + 0.015);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.28);
      const osc = ctx.createOscillator();
      osc.type = 'square';
      osc.frequency.setValueAtTime(180, now);
      osc.frequency.exponentialRampToValueAtTime(54, now + 0.22);
      osc.connect(gain);
      osc.start(now);
      osc.stop(now + 0.3);
      setTimeout(() => void ctx.close(), 480);
    } catch {
      // Optional audio.
    }
  }

  private playDodgeResultSound(success: boolean): void {
    try {
      const AudioContextCtor = window.AudioContext || (window as Window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
      if (!AudioContextCtor) return;
      const ctx = new AudioContextCtor();
      const now = ctx.currentTime;
      const gain = ctx.createGain();
      gain.connect(ctx.destination);
      gain.gain.setValueAtTime(0.0001, now);
      gain.gain.exponentialRampToValueAtTime(success ? 0.18 : 0.2, now + 0.018);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.36);
      const osc = ctx.createOscillator();
      osc.type = success ? 'sine' : 'triangle';
      osc.frequency.setValueAtTime(success ? 740 : 150, now);
      osc.frequency.exponentialRampToValueAtTime(success ? 1320 : 72, now + 0.24);
      osc.connect(gain);
      osc.start(now);
      osc.stop(now + 0.36);
      setTimeout(() => void ctx.close(), 560);
    } catch {
      // Optional audio.
    }
  }

  private playVictorySound(): void {
    try {
      const AudioContextCtor = window.AudioContext || (window as Window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
      if (!AudioContextCtor) return;
      const ctx = new AudioContextCtor();
      const now = ctx.currentTime;
      const master = ctx.createGain();
      master.connect(ctx.destination);
      master.gain.setValueAtTime(0.0001, now);
      master.gain.exponentialRampToValueAtTime(0.28, now + 0.025);
      master.gain.exponentialRampToValueAtTime(0.0001, now + 0.72);

      [523.25, 659.25, 783.99, 1046.5].forEach((frequency, index) => {
        const start = now + index * 0.085;
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = index === 3 ? 'sine' : 'triangle';
        osc.frequency.setValueAtTime(frequency, start);
        osc.frequency.exponentialRampToValueAtTime(frequency * 1.08, start + 0.12);
        gain.gain.setValueAtTime(0.0001, start);
        gain.gain.exponentialRampToValueAtTime(index === 3 ? 0.18 : 0.13, start + 0.018);
        gain.gain.exponentialRampToValueAtTime(0.0001, start + 0.26);
        osc.connect(gain);
        gain.connect(master);
        osc.start(start);
        osc.stop(start + 0.28);
      });

      const thump = ctx.createOscillator();
      thump.type = 'sine';
      thump.frequency.setValueAtTime(130, now);
      thump.frequency.exponentialRampToValueAtTime(58, now + 0.18);
      const thumpGain = ctx.createGain();
      thumpGain.gain.setValueAtTime(0.22, now);
      thumpGain.gain.exponentialRampToValueAtTime(0.0001, now + 0.22);
      thump.connect(thumpGain);
      thumpGain.connect(master);
      thump.start(now);
      thump.stop(now + 0.24);

      setTimeout(() => void ctx.close(), 900);
    } catch {
      // Celebration audio is optional and may be blocked until a user gesture.
    }
  }

  private handleSpinRejection(): void {
    const now = performance.now();
    if (now - this.spinRejectWindowStartedAt > 2000) { this.spinRejectWindowStartedAt = now; this.spinRejectCount = 0; }
    this.spinRejectCount += 1;
    if (this.spinRejectCount >= 3 && now - this.lastSpinWarningAt > 1200) {
      this.lastSpinWarningAt = now;
      this.statusText = 'Too much phone rotation. Do not swing the phone; hold it steady and move with your body.';
      this.queueRender();
    }
  }

  private clearSpinRejections(): void { this.spinRejectCount = 0; this.spinRejectWindowStartedAt = 0; this.lastSpinWarningAt = 0; }

  private pauseBattle(message: string): void {
    this.timer.pause();
    this.screen = 'paused';
    this.statusText = message;
    this.clearTimers(['stale', 'counter']);
    if (this.battlePhase === 'COUNTER_COUNTDOWN' || this.battlePhase === 'DODGE_WINDOW') {
      this.battlePhase = 'ACTIVE_ATTACK';
      this.counterCountdown = 0;
      this.counterDirection = null;
    }
    void this.releaseWakeLock();
    this.render();
  }

  private resumeBattle(): void {
    this.timer.resume();
    this.screen = 'battle';
    this.statusText = this.sensorMode === 'motion' ? 'Keep battling. Move the phone up and down with your body.' : 'Continue tap testing.';
    if (this.sensorMode === 'motion') this.refreshSensorStaleWatch();
    void this.requestWakeLock();
    this.render();
  }

  private finishRound(): void {
    this.clearTimers(['hit', 'stale', 'victory', 'counter']);
    const endedAt = performance.now();
    this.result = createResult({
      startedAt: this.timer.startedAt,
      endedAt,
      elapsedMs: this.timer.elapsed(endedAt),
      jumpCount: this.combat.jumpCount,
      targetJumps: this.config.targetJumps,
      defeated: this.combat.defeated,
      sensorMode: this.sensorMode
    });
    if (this.result.defeated) {
      this.resetDailyCaloriesIfNeeded();
      this.lastLevelBeforeRound = this.playerLevel();
      this.sitBreaksCompleted = Math.min(3, this.sitBreaksCompleted + 1);
      this.lastCaloriesEarned = this.estimateCalories(this.result.jumpCount, this.result.elapsedMs);
      this.lastXpEarned = this.experienceForRound(this.result);
      this.lastDamageDealt = this.damageDealtForRound();
      this.dailyCalories += this.lastCaloriesEarned;
      this.monthlyCalories += this.lastCaloriesEarned;
      this.totalCalories += this.lastCaloriesEarned;
      this.dailyXp += this.lastXpEarned;
      this.monthlyXp += this.lastXpEarned;
      this.totalXp += this.lastXpEarned;
      this.dailyDamage += this.lastDamageDealt;
      this.monthlyDamage += this.lastDamageDealt;
      this.totalDamage += this.lastDamageDealt;
      this.lastLevelAfterRound = this.playerLevel();
      this.totalSitBreaks += 1;
      this.monthlySitBreaks += 1;
      this.workoutsCompleted += 1;
      this.lastCoinsEarned = Math.round(20 * (1 + this.successfulDodges * DEFAULT_COUNTER_ATTACK_CONFIG.dodgeRewardBonusRate));
      this.coins += this.lastCoinsEarned;
      this.recordStreakCheckIn();
      this.sittingStartedAt = Date.now();
      this.sittingElapsedMs = 0;
      this.reminderBucketSent = 0;
    } else {
      this.lastCaloriesEarned = 0;
      this.lastCoinsEarned = 0;
      this.lastXpEarned = 0;
      this.lastDamageDealt = 0;
      this.lastLevelBeforeRound = this.playerLevel();
      this.lastLevelAfterRound = this.lastLevelBeforeRound;
    }
    this.persistProgress();
    this.syncBackgroundReminderIfEnabled();
    void this.syncLeaderboard();
    this.screen = 'results';
    this.stopMotion();
    void this.releaseWakeLock();
    this.render();
  }

  private replay(): void { this.screen = 'preflight'; this.statusText = 'Another round: confirm the area is safe, then start.'; this.render(); }
  private abandonRound(): void {
    this.clearTimers(['hit', 'stale', 'victory', 'counter']);
    this.stopMotion();
    void this.releaseWakeLock();
    this.result = null;
    this.screen = 'home';
    this.statusText = 'Session exited without completion. No sit-break credit was counted.';
    this.render();
  }
  private stopMotion(): void { this.motionStream?.stop(); this.motionStream = null; this.detector = null; }

  private refreshSensorStaleWatch(): void {
    if (this.staleTimer) clearTimeout(this.staleTimer);
    this.staleTimer = setTimeout(() => {
      if (this.screen === 'battle' && this.sensorMode === 'motion') this.pauseBattle('No motion data for 2 seconds, so the game is paused. Tap testing will remain available if motion is unsupported.');
    }, 2200);
  }

  private async requestWakeLock(): Promise<void> {
    const nav = navigator as NavigatorWithWakeLock;
    if (!nav.wakeLock || this.wakeLock) return;
    try { this.wakeLock = await nav.wakeLock.request('screen'); } catch { this.wakeLock = null; }
  }

  private async releaseWakeLock(): Promise<void> {
    if (!this.wakeLock) return;
    const sentinel = this.wakeLock;
    this.wakeLock = null;
    try { await sentinel.release(); } catch { /* optional */ }
  }

  private queueRender(): void {
    if (this.renderQueued) return;
    this.renderQueued = true;
    requestAnimationFrame(() => { this.renderQueued = false; this.render(); });
  }

  private clearTimers(which: Array<'hit' | 'stale' | 'victory' | 'calibration' | 'ultimate' | 'counter'> = ['hit', 'stale', 'victory', 'calibration', 'ultimate', 'counter']): void {
    if (which.includes('hit') && this.hitTimer) { clearTimeout(this.hitTimer); this.hitTimer = null; }
    if (which.includes('stale') && this.staleTimer) { clearTimeout(this.staleTimer); this.staleTimer = null; }
    if (which.includes('victory') && this.victoryTimer) { clearTimeout(this.victoryTimer); this.victoryTimer = null; }
    if (which.includes('calibration') && this.calibrationTimer) { clearTimeout(this.calibrationTimer); this.calibrationTimer = null; }
    if (which.includes('ultimate') && this.ultimateTimer) { clearTimeout(this.ultimateTimer); this.ultimateTimer = null; this.ultimateActive = false; }
    if (which.includes('counter') && this.counterTimer) { clearTimeout(this.counterTimer); this.counterTimer = null; this.counterCountdown = 0; }
  }

  private render(): void {
    const screenChanged = this.renderedScreen !== this.screen;
    const previousScrollTop = screenChanged ? 0 : (this.getScrollContainer()?.scrollTop ?? 0);
    this.root.innerHTML = `<main class="app-shell" data-screen="${this.screen}" data-outfit="${this.outfit}">${this.renderScreen()}${this.renderShellBottomNav()}<p class="sr-only" aria-live="polite">${this.statusText}</p></main>`;
    this.updateViewportVars();
    requestAnimationFrame(() => this.updateViewportVars());
    this.bindActions();
    if (screenChanged && this.screen !== 'battle' && !navigator.userAgent.includes('jsdom')) {
      requestAnimationFrame(() => {
        const container = this.getScrollContainer();
        if (container) container.scrollTo({ top: 0, left: 0, behavior: 'instant' as ScrollBehavior });
        window.scrollTo({ top: 0, left: 0 });
      });
    } else if (!screenChanged && previousScrollTop > 0) {
      const container = this.getScrollContainer();
      const savedScrollTop = this.screenScrollTop[this.screen] ?? previousScrollTop;
      if (container) container.scrollTop = savedScrollTop;
      requestAnimationFrame(() => {
        const nextContainer = this.getScrollContainer();
        if (nextContainer) nextContainer.scrollTop = savedScrollTop;
      });
    }
    if (screenChanged) this.screenScrollTop[this.screen] = 0;
    this.renderedScreen = this.screen;
  }

  private renderScreen(): string {
    switch (this.screen) {
      case 'select': return this.renderSelect();
      case 'workouts': return this.renderWorkouts();
      case 'preflight': return this.renderPreflight(false);
      case 'calibrating': return this.renderPreflight(true);
      case 'battle': return this.renderBattle();
      case 'paused': return this.renderPaused();
      case 'results': return this.renderResults();
      case 'home': return this.renderHome();
      case 'tasks': return this.renderTasks();
      case 'rank': return this.renderRank();
      case 'streak': return this.renderStreak();
      case 'me': return this.renderMe();
      case 'settings': return this.renderSettings();
      case 'outfit': return this.renderOutfit();
      case 'rewards': return this.renderRewards();
      case 'progress': return this.renderProgress();
    }
  }

  private renderSelect(): string {
    const urgency = this.sittingUrgency();
    const spec = exerciseSpecs[this.reminderExerciseId];
    const target = spec.defaultTarget;
    const calories = this.estimatedCaloriesForTarget(target, spec.id);
    const distance = spec.id === 'running' ? ` · ${this.formatDistance(this.estimatedRunningDistanceMeters(target))}` : '';
    return `<section class="start-screen reminder-screen urgency-${urgency}"><div class="reminder-atmosphere" aria-hidden="true"><i></i><i></i><i></i></div><header class="reminder-alert"><span>sit-break alert</span><strong>${this.formatSittingElapsed()}</strong></header><section class="reminder-stage"><div class="portal-rings" aria-hidden="true"><b></b><b></b><b></b></div><div class="floating-monster reminder-monster">${this.renderMonster('ready')}</div><div class="start-copy reminder-copy"><p class="eyebrow">New card draw</p><h1>Flip one<br><span>micro workout</span></h1><p>Your next monster break is ready.</p></div></section><article class="reminder-challenge-card"><button class="reminder-challenge-button" data-action="choose-reminder-workout" data-exercise="${spec.id}" aria-label="Choose ${spec.shortTitle}"><span class="reminder-challenge-copy"><small>Drawn challenge</small><strong>${spec.shortTitle}</strong><em>${target} ${spec.countLabel} · ~${calories} kcal${distance}</em></span><span class="reminder-challenge-art"><img src="${spec.icon}" alt="" decoding="async"></span></button></article><button class="primary-btn start-challenge reminder-start" data-action="choose-reminder-workout">Play</button><div class="sub-actions reminder-actions"><button class="text-btn" data-action="change-reminder-workout">Change</button><button class="text-btn" data-action="go-home">Home</button><button class="text-btn" data-action="tasks">Play list</button></div><p class="goal-note reminder-goal"><span>Today</span><strong>${this.dailyCalories}</strong> kcal · <strong>${this.sitBreaksCompleted}/3</strong> breaks</p></section>`;
  }
  private renderWorkouts(): string {
    const spec = this.currentSpec();
    const targetCards = spec.targets.map((target) => `<button class="target-card ${this.config.targetJumps === target ? 'active' : ''}" data-action="choose-target" data-target="${target}"><span>${target === spec.defaultTarget ? 'Base' : target === spec.targets[1] ? 'Workout' : 'Boss'}</span><strong>${target} ${spec.countLabel}</strong><img src="${this.officeMonsterAsset(target, 'idle')}" alt="" decoding="async"><em>~${this.estimatedCaloriesForTarget(target, spec.id)} kcal${spec.id === 'running' ? ` | ${this.formatDistance(this.estimatedRunningDistanceMeters(target))}` : ''}</em></button>`).join('');
    return `<section class="page-stack simple-page workouts-page"><header class="workout-hero"><div><p class="eyebrow">Battle setup</p><h1>${spec.shortTitle}</h1><p>${spec.instruction}</p></div><img src="${spec.icon}" alt="" decoding="async"></header><section class="panel workout-list"><div class="workout-list-heading"><div><p class="eyebrow">Monster contract</p><h2>Choose intensity</h2></div><span>Tap a card</span></div><p>Bigger goals summon stronger desk monsters.</p><div class="target-grid">${targetCards}</div></section></section>`;
  }
  private renderPreflight(isCalibrating: boolean): string {
    const spec = this.currentSpec();
    const calories = this.estimatedCaloriesForTarget(this.config.targetJumps as ExerciseTarget, spec.id);
    const distance = spec.id === 'running' ? ` · ${this.formatDistance(this.estimatedRunningDistanceMeters(this.config.targetJumps))}` : '';
    const primaryCue = spec.id === 'boxing'
      ? 'Punch forward with a firm grip'
      : spec.id === 'squat'
        ? 'Chest-height phone, drop then stand'
        : spec.id === 'running'
          ? 'Run in place, swing naturally'
          : spec.id === 'jumping_jacks'
            ? 'Open-close jumps, steady phone'
            : 'Jump naturally, phone in hand';
    return `<section class="page-stack tutorial-page ready-arena exercise-${spec.id}"><div class="ready-bg-orbit" aria-hidden="true"><i></i><i></i><i></i></div><header class="tutorial-hero ready-hero"><div><p class="eyebrow">Ready room</p><h1>${spec.shortTitle}<br><span>Battle</span></h1><p class="stat-line"><strong>${this.config.targetJumps}</strong> ${spec.countLabel} · ~${calories} kcal${distance}</p></div><div class="ready-monster">${this.renderMonster(isCalibrating ? 'alert' : 'ready')}</div></header><section class="ready-demo-card" aria-label="How to play"><div class="ready-phone-demo"><img class="ready-action-art" src="${spec.actionIcon}" alt="${spec.shortTitle} action pose" decoding="async"><span class="motion-trail one"></span><span class="motion-trail two"></span><span class="phone-signal"></span></div><img class="tutorial-image ready-image" src="${ART_ASSETS.tutorials[spec.id]}" alt="${spec.shortTitle} phone holding tutorial" decoding="async"><div class="ready-cue"><small>Motion guide</small><strong>${primaryCue}</strong></div></section><button class="primary-btn tutorial-start" data-action="begin-motion">${isCalibrating ? 'Calibrating…' : 'Start'}</button></section>`;
  }
  private renderHapticButton(value: HapticEvery, label: string): string {
    const active = this.config.hapticEvery === value;
    return `<button class="segmented ${active ? 'active' : ''}" data-action="set-haptic" data-haptic="${value}" aria-pressed="${active}">${label}</button>`;
  }

  private renderBattle(): string {
    const spec = this.currentSpec();
    const hp = hpPercent(this.combat);
    const hpBand = hp > 66 ? 'high' : hp > 33 ? 'mid' : 'low';
    const prompt = this.battlePhase === 'COUNTER_COUNTDOWN'
      ? 'STOP! Counter incoming'
      : this.battlePhase === 'DODGE_WINDOW'
        ? `DODGE ${this.counterDirection}`
        : this.battlePhase === 'DODGE_SUCCESS'
          ? 'Perfect Dodge!'
          : this.battlePhase === 'DODGE_FAILED'
            ? 'Dodge missed!'
            : this.ultimateActive ? 'POWER STRIKE!' : this.combat.jumpCount === 0 ? spec.instruction : spec.battleVerb;
    const monsterMood = this.enemyPose === 'defeated' ? 'defeated' : this.enemyPose === 'hit' ? 'hit' : 'angry';
    return `<section class="battle-page enemy-${this.enemyPose} phase-${this.battlePhase.toLowerCase().replaceAll('_', '-')} ${this.ultimateActive ? 'ultimate-active' : ''} exercise-${spec.id}"><header class="battle-top battle-top-clean"><p>${spec.shortTitle} attacks the desk monster</p></header><div class="battle-meter"><div class="hp-readout" aria-label="Monster HP ${Math.ceil(this.combat.enemyHp)} of ${this.combat.enemyMaxHp}"><span>Monster HP</span><strong>${Math.ceil(this.combat.enemyHp)}</strong><em>/ ${this.combat.enemyMaxHp}</em></div><h1>${prompt}</h1></div><button class="battle-stage" data-action="tap-jump" aria-label="${this.battlePhase === 'DODGE_WINDOW' ? 'Tap to simulate dodge' : 'Tap to simulate one attack'}"><div class="battle-avatar">${this.renderAvatar('battle')}</div><div class="rope-swoosh"></div><div class="impact-burst"></div><div class="ultimate-burst">HIT</div><div class="battle-monster">${this.renderMonster(monsterMood)}</div><div class="monster-hp hp-${hpBand}" aria-hidden="true"><b>Monster HP</b><i></i><span style="width:${hp}%"></span></div>${this.renderCounterOverlay()}</button><footer class="battle-actions no-finish"><button class="white-action" data-action="pause">II Pause</button></footer></section>`;
  }

  private renderCounterOverlay(): string {
    if (this.battlePhase === 'ACTIVE_ATTACK') return '';
    if (this.battlePhase === 'COUNTER_COUNTDOWN') {
      return `<div class="counter-overlay counter-charging"><div class="counter-aura"></div><p>Counter Attack</p><strong>${this.counterCountdown}</strong><span>Stop jumping. Prepare to dodge.</span></div>`;
    }
    if (this.battlePhase === 'DODGE_WINDOW') {
      return `<div class="counter-overlay dodge-window direction-${(this.counterDirection || 'UP').toLowerCase()}"><div class="counter-aura"></div><div class="slash-field"></div><p>Move phone</p><strong class="dodge-arrow" aria-hidden="true">${this.directionArrow(this.counterDirection)}</strong><span>${this.counterDirection} now</span></div>`;
    }
    if (this.battlePhase === 'DODGE_SUCCESS') {
      const bonus = Math.round(DEFAULT_COUNTER_ATTACK_CONFIG.dodgeRewardBonusRate * 100);
      const totalBonus = Math.round(this.successfulDodges * DEFAULT_COUNTER_ATTACK_CONFIG.dodgeRewardBonusRate * 100);
      return `<div class="counter-overlay dodge-result success"><p>Perfect Dodge</p><strong>+${bonus}% BONUS</strong><span>XP & coin bonus secured</span><small>Total dodge bonus: +${totalBonus}%</small></div>`;
    }
    const before = this.hpPercentFrom(this.lastCounterHpBeforeHeal || this.combat.enemyHp);
    const after = this.hpPercentFrom(this.lastCounterHpAfterHeal || this.combat.enemyHp);
    return `<div class="counter-overlay dodge-result failed" style="--heal-before:${before}%;--heal-after:${after}%"><div class="heal-beam" aria-hidden="true"></div><p>Dodge Missed</p><strong>MONSTER HEALS</strong><span>HP bar restored — no bonus</span><div class="heal-meter" aria-hidden="true"><i></i><b></b></div><small>Attack again to push the bar back down.</small></div>`;
  }

  private hpPercentFrom(enemyHp: number): number {
    return Math.max(0, Math.min(100, (enemyHp / this.combat.enemyMaxHp) * 100));
  }
  private renderPaused(): string {
    return `<section class="battle-page paused-page"><div class="pause-card"><h1>Paused</h1><p>${this.statusText}</p><button class="primary-btn" data-action="resume">Continue</button><button class="outline-btn" data-action="abandon-round">Exit - no completion</button></div></section>`;
  }

  private renderResults(): string {
    const result = this.result;
    const spec = this.currentSpec();
    const jumpCount = result?.jumpCount || this.config.targetJumps;
    const elapsed = result ? formatDuration(result.elapsedMs) : '0s';
    const dodgeBonus = Math.round(this.successfulDodges * DEFAULT_COUNTER_ATTACK_CONFIG.dodgeRewardBonusRate * 100);
    const leveledUp = this.lastLevelAfterRound > this.lastLevelBeforeRound;
    return `<section class="result-page victory-results"><header class="result-hero-card"><div class="result-copy"><div class="confetti">BATTLE CLEARED</div><h1>Victory!</h1><h2>${result?.defeated ? 'Desk Monster defeated' : 'Session saved'}</h2></div><div class="result-characters" aria-hidden="true">${this.renderAvatar('celebrate')}${this.renderMonster('defeated')}</div></header><section class="result-card result-scorecard"><span class="ribbon">${leveledUp ? `Level Up! Lv ${this.lastLevelAfterRound}` : 'Round Results'}</span><div class="result-grid"><div><span class="metric-icon">Count</span><strong>${jumpCount} ${spec.countLabel}</strong><span>${spec.shortTitle}</span></div><div><span class="metric-icon">Time</span><strong>${elapsed}</strong><span>Finished</span></div><div><span class="metric-icon">XP</span><strong>+${this.lastXpEarned} XP</strong><span>Lv ${this.playerLevel()} → ${this.xpToNextLevel()} next</span></div><div><span class="metric-icon">DMG</span><strong>${this.lastDamageDealt}</strong><span>damage dealt</span></div><div><span class="metric-icon">Kcal</span><strong>${this.lastCaloriesEarned} kcal</strong><span>burned</span></div><div><span class="metric-icon">Dodge</span><strong>+${dodgeBonus}%</strong><span>${this.successfulDodges} perfect</span></div></div></section><section class="recovery-card result-break-card">${this.renderMonster('happy')}<div><strong>Break logged</strong><div class="mini-progress"><span style="width:${Math.min(100, (this.sitBreaksCompleted / 3) * 100)}%"></span></div><p>${this.sitBreaksCompleted}/3 sit-breaks today</p></div><span class="coin-chip">+${this.lastCoinsEarned || 20}<small>coins</small></span></section><div class="result-actions single"><button class="primary-btn" data-action="go-home">Done</button></div></section>`;
  }
  private renderHome(): string {
    return `<section class="page-stack with-nav home-page"><section class="hero-card profile-card home-command"><button class="level-orb" data-action="progress"><small>Level</small><strong>${this.playerLevel()}</strong><span>${this.xpToNextLevel()} XP to next</span><i><b style="width:${Math.max(4, this.xpProgressPercent())}%"></b></i></button><div class="profile-avatar">${this.renderAvatar('portrait')}</div><div class="profile-copy"><p class="eyebrow">QuickFit Battle Pass</p><h1>${this.escapeHtml(this.playerName)} <button data-action="me" aria-label="Edit profile">Edit</button></h1><p>${this.dailyCalories} kcal · ${this.dailyXp} XP · ${this.dailyDamage} dmg</p><span>${this.sitBreaksCompleted}/3 sit-breaks completed</span><div class="mini-progress"><span style="width:${Math.min(100, (this.sitBreaksCompleted / 3) * 100)}%"></span></div><div class="hero-stats"><button data-action="streak"><small>Streak</small><strong>${this.currentStreakDays()}d</strong></button><button data-action="progress"><small>Breaks</small><strong>${this.sitBreaksCompleted}/3</strong></button><button data-action="outfit"><small>Coins</small><strong>${this.coins}</strong></button></div></div><div class="standing-avatar">${this.renderAvatar('standing')}</div><button class="change-outfit" data-action="outfit">Change Outfit</button></section>${this.renderSittingTimer()}<button class="primary-btn home-cta" data-action="tasks">Choose a workout</button><div class="home-grid home-grid-clean"><button class="panel weekly-panel" data-action="streak"><h2>Weekly Record <span>${this.currentStreakDays()}-day streak</span></h2>${this.renderWeekDots()}</button></div></section>`;
  }
  private renderWeekDots(): string {
    const checked = new Set(this.streakDates);
    const labels = ['M', 'T', 'W', 'T', 'F', 'S', 'S'];
    const today = new Date();
    const mondayOffset = (today.getDay() + 6) % 7;
    const monday = this.addDays(today, -mondayOffset);
    const days = labels.map((label, index) => {
      const day = this.addDays(monday, index);
      const done = checked.has(this.dateKey(day));
      const isToday = this.dateKey(day) === this.dateKey(today);
      return `<span><b class="${done ? 'done' : isToday ? 'today' : ''}">${done ? 'Done' : isToday ? 'Today' : ''}</b><em>${label}</em></span>`;
    }).join('');
    return `<div class="week-dots calendar">${days}</div>`;
  }

  private renderTasks(): string {
    const rows = (Object.values(exerciseSpecs) as Array<typeof exerciseSpecs[ExerciseId]>).map((spec) => `<button class="exercise-row simple-exercise" data-action="select-exercise" data-exercise="${spec.id}"><img src="${spec.icon}" alt="" decoding="async"><span><strong>${spec.title}</strong></span></button>`).join('');
    return `<section class="page-stack with-nav simple-page play-page main-tab-page"><h1>Play</h1><section class="panel exercise-list">${rows}</section></section>`;
  }

  private renderRank(): string {
    const tabs: Array<[TimedLeaderboardScope, string]> = [['today', 'Daily'], ['month', 'Monthly'], ['total', 'All-time']];
    return `<section class="page-stack with-nav simple-page social-page main-tab-page"><h1>Social</h1>${this.renderLeaderboardPanel({ board: 'xp', title: 'Level & XP leaderboard', featured: true, tabs: tabs.map(([scope, label]) => `<button class="${this.xpLeaderboardScope === scope ? 'active' : ''}" data-action="set-xp-leaderboard" data-scope="${scope}">${scope === 'total' ? 'Level' : `${label} XP`}</button>`).join(''), rows: this.rankedXpLeaderboardPlayers(this.xpLeaderboardScope), empty: 'Earn XP to join the board.', renderRow: (player, index) => this.renderXpLeaderboardRow(player, index, this.xpLeaderboardScope) })}${this.renderLeaderboardPanel({ board: 'damage', title: 'Damage leaderboard', tabs: tabs.map(([scope, label]) => `<button class="${this.damageLeaderboardScope === scope ? 'active' : ''}" data-action="set-damage-leaderboard" data-scope="${scope}">${label}</button>`).join(''), rows: this.rankedDamageLeaderboardPlayers(this.damageLeaderboardScope), empty: 'Deal damage to join the board.', renderRow: (player, index) => this.renderDamageLeaderboardRow(player, index, this.damageLeaderboardScope) })}${this.renderLeaderboardPanel({ board: 'breaks', title: 'Sit-break leaderboard', tabs: tabs.map(([scope, label]) => `<button class="${this.breakLeaderboardScope === scope ? 'active' : ''}" data-action="set-break-leaderboard" data-scope="${scope}">${label}</button>`).join(''), rows: this.rankedBreakLeaderboardPlayers(this.breakLeaderboardScope), empty: 'Finish a break to join the board.', renderRow: (player, index) => this.renderBreakLeaderboardRow(player, index, this.breakLeaderboardScope) })}${this.renderLeaderboardPanel({ board: 'kcal', title: 'Calories leaderboard', tabs: tabs.map(([scope, label]) => `<button class="${this.leaderboardScope === scope ? 'active' : ''}" data-action="set-leaderboard" data-scope="${scope}">${label}</button>`).join(''), rows: this.rankedLeaderboardPlayers(this.leaderboardScope), empty: 'Finish a workout to join the board.', renderRow: (player, index) => this.renderLeaderboardRow(player, index, this.leaderboardScope) })}</section>`;
  }

  private renderLeaderboardPanel(input: {
    board: LeaderboardBoard;
    title: string;
    rows: LeaderboardPlayer[];
    empty: string;
    featured?: boolean;
    tabs?: string;
    renderRow: (player: LeaderboardPlayer, index: number) => string;
  }): string {
    const expanded = this.expandedLeaderboards.has(input.board);
    const visibleRows = input.rows.slice(0, expanded ? 10 : 3);
    const rowHtml = visibleRows.map((player, index) => input.renderRow(player, index)).join('');
    const toggle = input.rows.length > 3
      ? `<button class="leaderboard-expand" data-action="toggle-leaderboard" data-board="${input.board}">${expanded ? 'Show top 3' : 'Show top 10'}</button>`
      : '';
    return `<section class="panel leaderboard-panel ${input.featured ? 'featured-board' : ''}"><div class="leaderboard-head"><h2>${input.title}</h2></div>${input.tabs ? `<div class="rank-tabs">${input.tabs}</div>` : ''}<div class="leaderboard-list">${rowHtml || `<p class="empty-state">${input.empty}</p>`}</div>${toggle}</section>`;
  }
  private renderStreak(): string {
    const checked = new Set(this.streakDates);
    const today = new Date();
    const visibleMonth = new Date(today.getFullYear(), today.getMonth() + this.streakMonthOffset, 1);
    const monthName = visibleMonth.toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
    const firstDayOffset = visibleMonth.getDay();
    const daysInMonth = new Date(visibleMonth.getFullYear(), visibleMonth.getMonth() + 1, 0).getDate();
    const cells = Array.from({ length: firstDayOffset }, () => '<div class="calendar-day empty"></div>');
    const days = Array.from({ length: daysInMonth }, (_, index) => {
      const day = new Date(visibleMonth.getFullYear(), visibleMonth.getMonth(), index + 1);
      const key = this.dateKey(day);
      const done = checked.has(key);
      const isToday = key === this.dateKey(today);
      return `<div class="calendar-day ${done ? 'done' : ''} ${isToday ? 'today' : ''}"><span>${day.getDate()}</span><strong>${done ? 'Done' : isToday ? 'Today' : ''}</strong></div>`;
    });
    return `<section class="page-stack simple-page"><h1>7-Day Streak</h1><section class="panel streak-summary"><span class="big-icon">Fire</span><div><h2>${this.currentStreakDays()} days</h2><p>One completed sit-break checks in the real-world calendar day.</p></div></section><section class="panel calendar-panel"><div class="calendar-head"><button data-action="streak-prev">Prev</button><h2>${monthName}</h2><button data-action="streak-next" ${this.streakMonthOffset >= 0 ? 'disabled' : ''}>Next</button></div><div class="calendar-weekdays"><span>Sun</span><span>Mon</span><span>Tue</span><span>Wed</span><span>Thu</span><span>Fri</span><span>Sat</span></div><div class="calendar-grid">${[...cells, ...days].join('')}</div></section></section>`;
  }

  private renderMe(): string {
    return `<section class="page-stack with-nav simple-page me-page main-tab-page"><h1>Me</h1><section class="panel me-card">${this.renderAvatar('portrait')}<h2>${this.escapeHtml(this.playerName)}</h2><label class="name-editor"><span>Display name</span><input data-name-input maxlength="24" value="${this.escapeHtml(this.playerName)}"></label><label><span>Gender</span><select data-profile-gender><option value="unset" ${this.gender === 'unset' ? 'selected' : ''}>Prefer not to say</option><option value="female" ${this.gender === 'female' ? 'selected' : ''}>Female</option><option value="male" ${this.gender === 'male' ? 'selected' : ''}>Male</option><option value="nonbinary" ${this.gender === 'nonbinary' ? 'selected' : ''}>Non-binary</option></select></label><label><span>Birthday</span><input type="date" data-profile-birth value="${this.birthDate}"></label><div class="profile-edit-grid"><label><span>Height cm</span><input type="number" min="80" max="240" data-profile-height value="${this.heightCm}"></label><label><span>Weight kg</span><input type="number" min="25" max="250" data-profile-weight value="${this.weightKg}"></label></div><p>Age: ${this.ageLabel()} | Outfit: ${outfitLabels[this.outfit]}</p><div class="profile-actions"><button class="primary-btn" data-action="save-profile">Save profile</button><button class="outline-btn" data-action="outfit">Change Outfit</button></div></section><section class="panel profile-stats"><h2>My activity</h2><p><strong>Lv ${this.playerLevel()}</strong><span>${this.totalXp} total XP</span></p><p><strong>${this.totalCalories}</strong><span>total kcal</span></p><p><strong>${this.totalSitBreaks}</strong><span>sit-breaks</span></p><p><strong>${this.currentStreakDays()}</strong><span>day streak</span></p></section><button class="primary-btn" data-action="settings">Settings</button></section>`;
  }
  private renderSettings(): string {
    const dndRows = this.dndWindows.map((window, index) => `<div class="dnd-row"><span>${window.start}-${window.end}</span><button data-action="remove-dnd" data-index="${index}">Remove</button></div>`).join('');
    const permission = 'Notification' in window ? Notification.permission : 'unsupported';
    const alertsOn = permission === 'granted';
    const notificationLabel = permission === 'unsupported' ? 'Unsupported' : alertsOn ? 'Alerts on' : 'Alerts off';
    return `<section class="page-stack simple-page settings-page"><h1>Settings</h1><section class="panel settings-list"><h2>Feedback</h2><p>Very light vibration on counted reps. Strong warning pulses only for monster counter attacks.</p><div class="settings-segments">${this.renderHapticButton(1, 'On')}${this.renderHapticButton(0, 'Off')}</div></section><section class="panel settings-list notification-settings"><h2>Notifications</h2><p class="settings-status" role="status">${this.escapeHtml(this.statusText)}</p><button class="switch-button ${alertsOn ? 'on' : ''}" data-action="enable-notifications" aria-pressed="${alertsOn}" ${permission === 'unsupported' ? 'disabled' : ''}><span></span><strong>${notificationLabel}</strong></button><p>Reminder interval</p><label><span>Minutes, 10-120</span><input type="number" min="10" max="120" data-reminder-interval value="${this.reminderIntervalMinutes}"></label><button data-action="save-reminder-settings">Save interval</button><p>Do-not-disturb pauses reminder popups during the quiet range.</p>${dndRows}<div class="profile-edit-grid"><label><span>Start</span><input type="time" data-dnd-start-new value="12:00"></label><label><span>End</span><input type="time" data-dnd-end-new value="15:00"></label></div><button data-action="add-dnd">Add do-not-disturb time</button></section></section>`;
  }

  private renderOutfit(): string {
    const options = (Object.keys(outfitLabels) as Outfit[]).map((outfit) => {
      const owned = this.ownedOutfits.includes(outfit);
      return `<button class="outfit-option ${this.outfit === outfit ? 'active' : ''}" data-action="set-outfit" data-outfit="${outfit}" aria-pressed="${this.outfit === outfit}"><span data-preview="${outfit}"><img src="${outfitAssets[outfit]}" alt="" decoding="async"></span><strong>${outfitLabels[outfit]}</strong><small>${this.outfit === outfit ? 'Equipped' : owned ? 'Owned' : `${outfitPrices[outfit]} coins`}</small></button>`;
    }).join('');
    return `<section class="page-stack simple-page"><h1>Change Outfit</h1><section class="panel outfit-preview">${this.renderAvatar('standing')}<div><h2>${outfitLabels[this.outfit]}</h2><p>Choose a look for your Active Hero.</p></div></section><div class="outfit-grid">${options}</div><button class="primary-btn" data-action="go-home">Save Outfit</button></section>`;
  }

  private renderRewards(): string {
    return `<section class="page-stack simple-page"><h1>Coins</h1><section class="panel coin-shop"><h2>Coin Shop</h2><p>Balance: <strong>${this.coins}</strong></p><button data-action="outfit">Browse outfits</button></section></section>`;
  }

  private renderProgress(): string {
    return `<section class="page-stack simple-page"><h1>Today's Progress</h1><section class="panel progress-detail"><div class="ring large" style="--ring:${Math.max(8, (this.sitBreaksCompleted / 3) * 100)}%">Move</div><h2>${this.sitBreaksCompleted}/3 sit-breaks completed</h2><p>Small movement breaks help reset posture and energy.</p><button class="primary-btn" data-action="choose-jump-rope">Do another break</button></section></section>`;
  }

  private rankedLeaderboardPlayers(scope: TimedLeaderboardScope = this.leaderboardScope): LeaderboardPlayer[] {
    const scoreKey = scope === 'month' ? 'monthCalories' : scope === 'total' ? 'totalCalories' : 'todayCalories';
    return this.uniqueLeaderboardPlayers().sort((a, b) => (b[scoreKey] ?? 0) - (a[scoreKey] ?? 0));
  }

  private rankedBreakLeaderboardPlayers(scope: TimedLeaderboardScope = this.breakLeaderboardScope): LeaderboardPlayer[] {
    return this.uniqueLeaderboardPlayers().sort((a, b) => this.breakScore(b, scope) - this.breakScore(a, scope));
  }

  private toggleLeaderboard(board: LeaderboardBoard): void {
    if (this.expandedLeaderboards.has(board)) this.expandedLeaderboards.delete(board);
    else this.expandedLeaderboards.add(board);
    this.render();
  }

  private rankedXpLeaderboardPlayers(scope: TimedLeaderboardScope = this.xpLeaderboardScope): LeaderboardPlayer[] {
    return this.uniqueLeaderboardPlayers().sort((a, b) => this.xpScore(b, scope) - this.xpScore(a, scope));
  }

  private rankedDamageLeaderboardPlayers(scope: TimedLeaderboardScope = this.damageLeaderboardScope): LeaderboardPlayer[] {
    return this.uniqueLeaderboardPlayers().sort((a, b) => this.damageScore(b, scope) - this.damageScore(a, scope));
  }

  private uniqueLeaderboardPlayers(): LeaderboardPlayer[] {
    const byId = new Map<string, LeaderboardPlayer>();
    [...this.leaderboardPlayers, this.localLeaderboardPlayer()].forEach((player) => {
      if (!player?.playerId) return;
      byId.set(player.playerId, this.normalizeLeaderboardPlayer({ ...byId.get(player.playerId), ...player }));
    });
    return [...byId.values()];
  }

  private breakScore(player: LeaderboardPlayer, scope: TimedLeaderboardScope): number {
    if (scope === 'today') return player.todayBreaks ?? 0;
    if (scope === 'month') return player.monthBreaks ?? 0;
    return player.breaks ?? 0;
  }

  private xpScore(player: LeaderboardPlayer, scope: TimedLeaderboardScope): number {
    if (scope === 'today') return player.todayXp ?? 0;
    if (scope === 'month') return player.monthXp ?? 0;
    return player.totalXp ?? player.level ?? 0;
  }

  private damageScore(player: LeaderboardPlayer, scope: TimedLeaderboardScope): number {
    if (scope === 'today') return player.todayDamage ?? 0;
    if (scope === 'month') return player.monthDamage ?? 0;
    return player.totalDamage ?? 0;
  }

  private renderXpLeaderboardRow(player: LeaderboardPlayer, index: number, scope: TimedLeaderboardScope = this.xpLeaderboardScope): string {
    const isSelf = player.playerId === this.playerId;
    const level = player.level ?? this.playerLevel(player.totalXp ?? 0);
    const score = scope === 'total' ? level : this.xpScore(player, scope);
    const detail = scope === 'total' ? `${player.totalXp ?? 0} total XP` : `${scope === 'today' ? 'today' : 'month'} XP`;
    return this.renderLeaderboardRowShell(player, index, isSelf, score, scope === 'total' ? 'level' : 'xp', detail);
  }

  private renderDamageLeaderboardRow(player: LeaderboardPlayer, index: number, scope: TimedLeaderboardScope = this.damageLeaderboardScope): string {
    const isSelf = player.playerId === this.playerId;
    return this.renderLeaderboardRowShell(player, index, isSelf, this.damageScore(player, scope), 'damage', scope === 'today' ? 'today damage' : scope === 'month' ? 'month damage' : 'total damage');
  }
  private renderLeaderboardRow(player: LeaderboardPlayer, index: number, scope: TimedLeaderboardScope = this.leaderboardScope): string {
    const isSelf = player.playerId === this.playerId;
    const score = scope === 'month' ? player.monthCalories : scope === 'total' ? player.totalCalories : player.todayCalories;
    return this.renderLeaderboardRowShell(player, index, isSelf, score || 0, 'kcal', 'calories');
  }

  private renderBreakLeaderboardRow(player: LeaderboardPlayer, index: number, scope: TimedLeaderboardScope = this.breakLeaderboardScope): string {
    const isSelf = player.playerId === this.playerId;
    return this.renderLeaderboardRowShell(player, index, isSelf, this.breakScore(player, scope), 'breaks', scope === 'today' ? 'today breaks' : scope === 'month' ? 'month breaks' : 'total breaks');
  }

  private renderLeaderboardRowShell(player: LeaderboardPlayer, index: number, isSelf: boolean, score: number, unit: 'kcal' | 'breaks' | 'xp' | 'damage' | 'level', detail: string): string {
    const label = unit === 'level' ? `Lv ${score || 1}` : unit === 'xp' ? `${score || 0} XP` : unit === 'damage' ? `${score || 0}` : `${score || 0} ${unit}`;
    return `<div class="leaderboard-row ${isSelf ? 'self' : ''}"><span class="rank-index">#${index + 1}</span><span class="leader-avatar">${this.renderAvatar('mini', player.outfit)}</span><span><strong>${this.escapeHtml(player.name)}</strong><em>${isSelf ? 'You' : `${player.streak || 0}-day streak`}</em></span><mark>${label}<small>${detail}</small></mark></div>`;
  }
  private renderShellBottomNav(): string {
    const active = this.mainTabNavActive();
    return active ? this.renderBottomNav(active) : '';
  }

  private mainTabNavActive(): 'home' | 'tasks' | 'rank' | 'me' | null {
    switch (this.screen) {
      case 'home': return 'home';
      case 'tasks': return 'tasks';
      case 'rank': return 'rank';
      case 'me': return 'me';
      default: return null;
    }
  }

  private renderBottomNav(active: 'home' | 'tasks' | 'rank' | 'me'): string {
    const icons = {
      home: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 11.5 12 5l8 6.5V20h-5v-5H9v5H4z"/></svg>',
      tasks: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M8 4h8l2 4 3 2-4 10H7L3 10l3-2zM9 8l3 3 3-3"/></svg>',
      rank: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M5 20V9h4v11zm5 0V4h4v16zm5 0v-7h4v7z"/></svg>',
      me: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 12a4 4 0 1 0 0-8 4 4 0 0 0 0 8m-7 8c1.2-4 4-6 7-6s5.8 2 7 6z"/></svg>'
    } as const;
    const items = [['home', 'Home'], ['tasks', 'Play'], ['rank', 'Social'], ['me', 'Me']] as const;
    return `<nav class="bottom-nav" aria-label="Main navigation">${items.map(([id, label]) => `<button class="${active === id ? 'active' : ''}" data-action="${id}"><span>${icons[id]}</span><small>${label}</small></button>`).join('')}</nav>`;
  }

  private renderAvatar(size: 'mini' | 'portrait' | 'standing' | 'large' | 'battle' | 'celebrate', outfit: Outfit | string = this.outfit): string {
    const src = size === 'battle' ? this.currentSpec().actionIcon : size === 'large' ? ART_ASSETS.heroAttack : this.outfitAssetFor(outfit);
    return `<img class="avatar-art avatar-${size}" src="${src}" alt="Active Hero" decoding="async">`;
  }

  private outfitAssetFor(outfit: Outfit | string): string {
    return this.isKnownOutfit(outfit) ? outfitAssets[outfit] : outfitAssets.teal;
  }

  private isKnownOutfit(outfit: Outfit | string | undefined): outfit is Outfit {
    return typeof outfit === 'string' && outfit in outfitAssets;
  }

  private renderMonster(mood: 'happy' | 'ready' | 'alert' | 'angry' | 'hit' | 'defeated'): string {
    const target = (jumpTargets.includes(this.config.targetJumps as JumpTarget) ? this.config.targetJumps : 100) as JumpTarget;
    const src = mood === 'defeated'
      ? this.officeMonsterAsset(target, 'defeated')
      : mood === 'hit'
        ? this.officeMonsterAsset(target, 'hit')
        : this.officeMonsterAsset(target, 'idle');
    return `<img class="monster-art monster-${mood}" src="${src}" alt="Lazy Monster" decoding="async">`;
  }

  private officeMonsterAsset(target: number, pose: 'idle' | 'hit' | 'defeated'): string {
    const spec = this.currentSpec();
    const index = spec.targets.findIndex((candidate) => candidate === target);
    const tier: JumpTarget = index >= 2 ? 1000 : index === 1 ? 300 : 100;
    return ART_ASSETS.officeMonsters[tier][pose];
  }
  private bindActions(): void {
    const buttons = this.root.querySelectorAll<HTMLButtonElement>('[data-action]');
    buttons.forEach((button) => {
      button.addEventListener('click', () => {
        vibrateForTap(this.config.hapticEvery !== 0);
        const action = button.dataset.action;
        switch (action) {
          case 'go-home': case 'home': this.goHome(); break;
          case 'tasks': this.goMainTab('tasks', 'Tasks opened.'); break;
          case 'workouts': this.currentExerciseId = 'jump_rope'; this.goToScreen('workouts', 'Choose a workout.'); break;
          case 'rank': this.goMainTab('rank', 'Social opened.'); break;
          case 'streak': this.goToScreen('streak', 'Streak calendar opened.'); break;
          case 'me': this.goMainTab('me', 'Profile opened.'); break;
          case 'settings': this.goToScreen('settings', 'Settings opened.'); break;
          case 'outfit': this.goToScreen('outfit', 'Outfit closet opened.'); break;
          case 'rewards': this.goToScreen('rewards', 'Rewards opened.'); break;
          case 'progress': this.goToScreen('progress', 'Progress opened.'); break;
          case 'back-select': this.goToScreen('select', "You've been sitting for 60 minutes. Choose a micro-workout."); break;
          case 'choose-jump-rope': this.chooseJumpRope(100); break;
          case 'choose-reminder-workout': this.chooseReminderWorkout(); break;
          case 'select-exercise': this.currentExerciseId = (button.dataset.exercise as ExerciseId) || 'jump_rope'; this.setExerciseTarget(this.currentExerciseId, this.currentSpec().defaultTarget); this.goToScreen('workouts', `${this.currentSpec().shortTitle} opened.`); break;
          case 'choose-target': this.chooseExercise(this.currentExerciseId, Number(button.dataset.target) as ExerciseTarget); break;
          case 'change-reminder-workout': this.redrawReminderWorkout(); break;
          case 'locked-card': this.handleLockedExercise(button.dataset.title || 'This workout'); break;
          case 'begin-motion': void this.beginMotionFlow(); break;
          case 'set-haptic': this.setHapticEvery(Number(button.dataset.haptic) as HapticEvery); break;
          case 'set-xp-leaderboard': void this.loadXpLeaderboard(button.dataset.scope as TimedLeaderboardScope); break;
          case 'set-leaderboard': void this.loadLeaderboard(button.dataset.scope as TimedLeaderboardScope); break;
          case 'set-break-leaderboard': void this.loadBreakLeaderboard(button.dataset.scope as TimedLeaderboardScope); break;
          case 'set-damage-leaderboard': void this.loadDamageLeaderboard(button.dataset.scope as TimedLeaderboardScope); break;
          case 'toggle-leaderboard': this.toggleLeaderboard(button.dataset.board as LeaderboardBoard); break;
          case 'invite-friend': this.statusText = 'Invite link ready: share this app URL with a friend.'; this.render(); break;
          case 'copy-player-id': void navigator.clipboard?.writeText?.(this.playerId).catch(() => undefined); this.statusText = 'Player ID copied when clipboard is available.'; this.render(); break;
          case 'save-name': this.saveName(); break;
          case 'save-profile': this.saveProfile(); break;
          case 'save-reminder-settings': this.saveReminderSettings(); break;
          case 'add-dnd': this.addDndWindow(); break;
          case 'remove-dnd': this.removeDndWindow(Number(button.dataset.index)); break;
          case 'streak-prev': this.streakMonthOffset -= 1; this.render(); break;
          case 'streak-next': if (this.streakMonthOffset < 0) this.streakMonthOffset += 1; this.render(); break;
          case 'set-outfit': this.setOutfit(button.dataset.outfit as Outfit); break;
          case 'read-tip': this.readWellnessTip(); break;
          case 'enable-notifications': void this.enableNotifications(); break;
          case 'reset-progress': this.resetLocalProgress(); break;
          case 'start-fallback': this.startTapFallback(); break;
          case 'tap-jump': this.registerJump(); break;
          case 'pause': this.pauseBattle('Paused manually.'); break;
          case 'resume': this.resumeBattle(); break;
          case 'abandon-round': this.abandonRound(); break;
          case 'replay': this.replay(); break;
        }
      });
    });
  }
}

